import React, { useState, useEffect } from 'react';
import './App.css';
import LiveDemo from './LiveDemo';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, push } from 'firebase/database';

// Firebase Configuration (Realtime Database)
const firebaseConfig = {
  apiKey: "AIzaSyDti6opQ2liqHo-VxRGwj0w_7XexgRuDbA",
  authDomain: "ascona-16526.firebaseapp.com",
  databaseURL: "https://ascona-16526-default-rtdb.firebaseio.com",
  projectId: "ascona-16526",
  storageBucket: "ascona-16526.firebasestorage.app",
  messagingSenderId: "477580922753",
  appId: "1:477580922753:web:8b973e70dc6685a9b9a280",
  measurementId: "G-EVS6196YHD"
};

const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// Simulator Presets (Integrated with Desktop/ascona schemas)
const SIMULATOR_PRESETS = [
  {
    id: 'voice-agent',
    title: 'AI Voice Agent Pipeline',
    tag: 'Infrastructure & AI',
    prompt: 'Build a WebRTC real-time voice assistant with low-latency streaming audio, speaker diarization, fallback to REST on network disruption, and audio redaction for PII compliance.',
    epic: {
      key: 'EPIC-410',
      title: 'Low-Latency WebRTC Voice Pipeline & PII Guardrails',
      description: 'Decompose core streaming voice infrastructure, connection health monitor, and real-time PII redaction layer.'
    },
    stories: [
      {
        key: 'ASC-104',
        title: 'WebRTC Peer Connection & Streaming Audio Buffer',
        points: 5,
        storyPoints: 5,
        sprint: 1,
        labels: ['realtime', 'webrtc', 'p0'],
        prerequisites: [],
        type: 'Story',
        description: 'As a voice platform engineer, I want bi-directional WebRTC peer connections with jitter-resilient Opus audio buffers, so that callers experience natural turn-taking with sub-250ms roundtrip audio latency.',
        gherkin: `Scenario: Graceful codec degradation on network latency spike
Given an active caller session over WebRTC
When round-trip network latency spikes >250ms for 3 consecutive intervals
Then seamlessly downgrade audio codec from Opus-Fullband to Opus-Narrowband without dropped frames
And emit a telemetry ping to the Datadog voice quality dashboard`
      },
      {
        key: 'ASC-105',
        title: 'Real-Time Speaker Diarization & Audio Redaction',
        points: 8,
        storyPoints: 8,
        sprint: 1,
        labels: ['compliance', 'audio', 'p0'],
        prerequisites: ['ASC-104'],
        type: 'Story',
        description: 'As a security compliance officer, I want payment card and SSN audio bytes detected and masked within 40ms before streaming to cloud LLMs, so that our voice platform adheres strictly to PCI DSS standards.',
        gherkin: `Scenario: Automated redaction of sensitive payment card data
Given an incoming 16kHz audio stream from the customer channel
When credit card numbers or SSN patterns are recognized in audio buffer
Then mute audio buffer slice, mask transcript to [REDACTED]
And forward sanitized stream to the LLM context buffer within 40ms`
      },
      {
        key: 'ASC-106',
        title: 'Offline Fallback & State Recovery Mechanism',
        points: 3,
        storyPoints: 3,
        sprint: 2,
        labels: ['resilience', 'mobile', 'p1'],
        prerequisites: ['ASC-104'],
        type: 'Story',
        description: 'As an end-user on an unstable mobile network, I want the client to seamlessly fall back to HTTP streaming if WebRTC ICE connection fails, so that my call session is preserved without interruption.',
        gherkin: `Scenario: Connection drop recovery
Given sudden WebRTC socket termination
When ICE reconnection fails within 3.0 seconds
Then switch client to fallback HTTP polling, preserve conversation memory
And prompt agent UI with state recovery badge`
      }
    ],
    prfaq: `# AMAZON-STYLE PRFAQ: Real-Time AI Voice Agent Platform
FOR IMMEDIATE RELEASE: Ascona AI Unveils Ultra-Low-Latency WebRTC Voice Pipeline

SAN FRANCISCO — Ascona AI today announced its sub-250ms voice orchestration agent, enabling customer care teams to deploy natural audio AI with zero PII leakage risk.

Customer Problem:
Legacy telephony IVRs and HTTP-based speech pipelines suffer from 800ms+ turn-taking latencies, leading to awkward conversational collision. Simultaneously, financial regulators demand instantaneous on-device or edge redaction of payment data before audio buffers reach cloud LLM servers.

Solution & Architecture:
Ascona AI establishes bi-directional WebRTC peer connections with jitter-resilient audio buffers. Audio streams pass through an in-memory 16kHz VAD (Voice Activity Detection) worker that redacts card numbers within 40ms, fallbacking to async HTTP polling if ICE negotiations degrade.

Internal Engineering FAQ:
Q1: What are our latency SLAs?
A: P95 roundtrip audio latency is guaranteed under 280ms under standard 4G/LTE conditions.
Q2: How does PII redaction protect against false positives?
A: The acoustic classifier uses dual-pass phoneme verification before masking audio slices.`,
    sprintPlan: [
      {
        "Story": "Sprint I: WebRTC Transport",
        "Expected Tasks": "Signaling server setup, ICE gathering, Opus buffer allocation",
        "Resource Name": "Voice Infra Lead",
        "Sprint ID": "SPR-01",
        "Plan Start Date": "2026-10-14",
        "Actual Start Date": "2026-10-15",
        "Plan End Date": "2026-10-26",
        "Actual End Date": "2026-10-26",
        "Ticket ID": "ASC-104",
        "Estimate (in Days)": 5,
        "Deliverable": "WebRTC Media Gateway",
        "Status": "In Progress",
        "Artifacts": "RFC-8829 Signaling Spec",
        "Adjusted Estimate": 5,
        "Actual Effort (Days)": 5,
        "Comments": "Coturn server provisioned in US-East"
      },
      {
        "Story": "Sprint II: PII Redaction Worker",
        "Expected Tasks": "16kHz audio buffer stream masking, PCI DSS tokenization",
        "Resource Name": "ML / Audio Engineer",
        "Sprint ID": "SPR-01",
        "Plan Start Date": "2026-10-27",
        "Actual Start Date": "2026-10-28",
        "Plan End Date": "2026-11-09",
        "Actual End Date": "2026-11-09",
        "Ticket ID": "ASC-105",
        "Estimate (in Days)": 8,
        "Deliverable": "Edge Audio Filter",
        "Status": "Ready for Review",
        "Artifacts": "Redaction benchmark report",
        "Adjusted Estimate": 8,
        "Actual Effort (Days)": 7,
        "Comments": "Target latency under 40ms validated"
      },
      {
        "Story": "Sprint III: Fallback & Telemetry",
        "Expected Tasks": "REST polling fallback on socket close, Datadog metric hooks",
        "Resource Name": "Full-Stack Engineer",
        "Sprint ID": "SPR-02",
        "Plan Start Date": "2026-11-10",
        "Actual Start Date": "2026-11-10",
        "Plan End Date": "2026-11-20",
        "Actual End Date": "2026-11-20",
        "Ticket ID": "ASC-106",
        "Estimate (in Days)": 3,
        "Deliverable": "Resilience Middleware",
        "Status": "Backlog",
        "Artifacts": "E2E Chaos Test Suite",
        "Adjusted Estimate": 3,
        "Actual Effort (Days)": 3,
        "Comments": "Auto-recovery within 3 seconds"
      }
    ],
    cards: [
      { name: "WebRTC Peer Signaling Gateway", text: "Implement STUN/TURN server handshake and adaptive bitrate Opus codec.", points: 5 },
      { name: "Real-Time PII Audio Redaction", text: "Mask credit card and SSN audio bytes before dispatching to LLM context.", points: 8 },
      { name: "Offline Polling Fallback", text: "Graceful state machine downgrade from WebRTC to HTTP streaming.", points: 3 }
    ]
  },
  {
    id: 'stripe-billing',
    title: 'Stripe Metered Billing Engine',
    tag: 'Fintech & SaaS',
    prompt: 'Implement usage-based billing with Stripe Metered APIs, handling credit card updates, proration calculations on mid-cycle upgrades, and automated dunning emails via SendGrid.',
    epic: {
      key: 'EPIC-411',
      title: 'Metered Subscription Engine & Dunning Workflows',
      description: 'Production-ready usage metering, idempotent event ingestion, and failed payment recovery workflows.'
    },
    stories: [
      {
        key: 'ASC-201',
        title: 'Usage Record Ingestion & Stripe Idempotent Aggregator',
        points: 5,
        storyPoints: 5,
        sprint: 1,
        labels: ['fintech', 'stripe', 'p0'],
        prerequisites: [],
        type: 'Story',
        description: 'As a billing platform engineer, I want high-throughput usage events deduplicated with Redis bloom filters and batched into hourly Stripe Metered records, so that billing leakage and duplicate charges are eliminated.',
        gherkin: `Scenario: High-throughput usage event ingestion
Given a flood of 50,000 API usage events per hour
When usage records are posted to /v1/billing/usage
Then deduplicate via idempotency_key, batch aggregate units
And push usage records to Stripe Metered API without exceeding rate limits`
      },
      {
        key: 'ASC-202',
        title: 'Mid-Cycle Plan Upgrade & Proration Calculations',
        points: 8,
        storyPoints: 8,
        sprint: 1,
        labels: ['billing', 'proration', 'p0'],
        prerequisites: ['ASC-201'],
        type: 'Story',
        description: 'As a SaaS customer upgrading subscription tiers mid-cycle, I want unused plan credits prorated to the exact second and net invoices charged immediately, so that my renewal anniversary remains consistent.',
        gherkin: `Scenario: Immediate tier upgrade with prorated credits
Given an active customer on Starter Plan ($49/mo) with 12 days remaining in cycle
When user confirms upgrade to Scale Plan ($299/mo)
Then calculate unused credit proration down to second
And charge net delta immediately while preserving renewal anniversary`
      },
      {
        key: 'ASC-203',
        title: 'Automated Dunning Retries & Webhook Fallback',
        points: 3,
        storyPoints: 3,
        sprint: 2,
        labels: ['dunning', 'sendgrid', 'p1'],
        prerequisites: ['ASC-201'],
        type: 'Story',
        description: 'As a revenue operations manager, I want automated exponential card retry schedules and SendGrid 1-click update emails triggered on failed invoices, so that involuntary churn is reduced by 60%.',
        gherkin: `Scenario: Failed invoice recovery sequence
Given a Stripe webhook invoice.payment_failed event
When invoice retry count is under 4 attempts
Then execute exponential backoff retry schedule
And dispatch transactional dunning email with 1-click update link via SendGrid`
      }
    ],
    prfaq: `# AMAZON-STYLE PRFAQ: Automated Metered SaaS Billing Platform
FOR IMMEDIATE RELEASE: Ascona AI Introduces Real-Time Stripe Usage Ingestion Engine

SAN FRANCISCO — Ascona AI today launched its idempotent usage metering pipeline, eliminating billing leakage and disputes for high-throughput enterprise developer platforms.

Customer Problem:
Modern usage-based SaaS companies lose thousands of dollars each month in unmetered API calls, while experiencing manual reconciliation nightmares when enterprise customers upgrade subscription tiers mid-billing cycle.

Solution & Architecture:
An asynchronous event ingestion pipeline backed by Redis bloom filters deduplicates billions of API hits, batches them into hourly Stripe Metered API summaries, and automatically computes second-by-second prorations for tier changes.

Internal Engineering FAQ:
Q1: How do we prevent Stripe API rate limit exhaustion?
A: Ingestion events are buffered in memory and flushed via sliding-window batch operations.
Q2: What happens if an invoice payment fails?
A: Smart dunning logic schedules automated card retries at optimal intervals while alerting the customer.`,
    sprintPlan: [
      {
        "Story": "Sprint I: Ingestion Pipeline",
        "Expected Tasks": "Redis idempotency filter, queue consumer, batch aggregator",
        "Resource Name": "Fintech Backend Lead",
        "Sprint ID": "SPR-01",
        "Plan Start Date": "2026-10-14",
        "Actual Start Date": "2026-10-14",
        "Plan End Date": "2026-10-24",
        "Actual End Date": "2026-10-24",
        "Ticket ID": "ASC-201",
        "Estimate (in Days)": 5,
        "Deliverable": "Usage Event Ingestion API",
        "Status": "In Progress",
        "Artifacts": "Load test results (100k req/sec)",
        "Adjusted Estimate": 5,
        "Actual Effort (Days)": 4,
        "Comments": "Idempotency keys enforced in DB schema"
      },
      {
        "Story": "Sprint II: Proration Engine",
        "Expected Tasks": "Mid-cycle upgrade calculation, invoice preview endpoint",
        "Resource Name": "Billing Engineer",
        "Sprint ID": "SPR-01",
        "Plan Start Date": "2026-10-25",
        "Actual Start Date": "2026-10-25",
        "Plan End Date": "2026-11-05",
        "Actual End Date": "2026-11-05",
        "Ticket ID": "ASC-202",
        "Estimate (in Days)": 5,
        "Deliverable": "Proration Calculation Service",
        "Status": "Ready for Review",
        "Artifacts": "Accounting audit test cases",
        "Adjusted Estimate": 5,
        "Actual Effort (Days)": 5,
        "Comments": "Second-level timestamp accuracy verified"
      },
      {
        "Story": "Sprint III: Dunning & Notifications",
        "Expected Tasks": "Webhook listeners, SendGrid template bindings, retry schedule",
        "Resource Name": "Full-Stack Engineer",
        "Sprint ID": "SPR-02",
        "Plan Start Date": "2026-11-06",
        "Actual Start Date": "2026-11-06",
        "Plan End Date": "2026-11-15",
        "Actual End Date": "2026-11-15",
        "Ticket ID": "ASC-203",
        "Estimate (in Days)": 3,
        "Deliverable": "Automated Dunning Workflow",
        "Status": "Backlog",
        "Artifacts": "Webhook security unit tests",
        "Adjusted Estimate": 3,
        "Actual Effort (Days)": 3,
        "Comments": "Sign-off from revenue operations team"
      }
    ],
    cards: [
      { name: "Idempotent Usage Ingestion Queue", text: "Batch 50,000 usage events/hr into Stripe Metered API with zero duplication.", points: 5 },
      { name: "Mid-Cycle Proration Calculator", text: "Compute exact second-level tier changes and charge net invoice delta.", points: 5 },
      { name: "Smart Dunning Retries & Alerts", text: "Automatic exponential retry on failed charges with SendGrid 1-click update link.", points: 3 }
    ]
  },
  {
    id: 'enterprise-rbac',
    title: 'Okta SAML & Tenant RBAC',
    tag: 'Enterprise Security',
    prompt: 'Add Okta SAML 2.0 / SCIM user provisioning, tenant database schema isolation, and custom role permissions (Admin, Editor, Auditor) with audit logging.',
    epic: {
      key: 'EPIC-412',
      title: 'Enterprise Identity & Zero-Trust RBAC Architecture',
      description: 'SCIM 2.0 provisioning lifecycle, multi-tenant schema partitioning, and tamper-evident audit logging.'
    },
    stories: [
      {
        key: 'ASC-301',
        title: 'Okta SAML 2.0 & SCIM 2.0 Directory Sync',
        points: 8,
        storyPoints: 8,
        sprint: 1,
        labels: ['security', 'saml', 'p0'],
        prerequisites: [],
        type: 'Story',
        description: 'As an enterprise IT administrator, I want employee accounts provisioned automatically via Okta SCIM within 5 seconds of group membership changes, so that user onboarding requires zero manual IT intervention.',
        gherkin: `Scenario: Automated employee onboarding via Okta SCIM
Given an Enterprise customer with Okta SCIM integration configured
When IT administrator adds employee to Ascona-Admins Okta group
Then provision user account in under 5 seconds
And automatically bind role mapping to Organization Admin role with MFA required`
      },
      {
        key: 'ASC-302',
        title: 'Row-Level Tenant Isolation & Partition Enforcement',
        points: 5,
        storyPoints: 5,
        sprint: 1,
        labels: ['database', 'security', 'p0'],
        prerequisites: ['ASC-301'],
        type: 'Story',
        description: 'As a security architect, I want PostgreSQL Row-Level Security (RLS) policies enforced at the connection pool level, so that cross-tenant data leakage is cryptographically impossible.',
        gherkin: `Scenario: Prevention of cross-tenant data leakage
Given an authenticated request carrying Org A JWT token
When database query executes across shared tables
Then Postgres RLS (Row Level Security) must enforce tenant_id = Org A
And raise an alert if query attempts cross-tenant boundary access`
      },
      {
        key: 'ASC-303',
        title: 'Immutable Cryptographic Security Audit Log Pipeline',
        points: 3,
        storyPoints: 3,
        sprint: 2,
        labels: ['audit', 'soc2', 'p1'],
        prerequisites: ['ASC-302'],
        type: 'Story',
        description: 'As a compliance auditor, I want every privilege escalation and role modification written to an append-only hash-chained ledger and mirrored to S3 Glacier, so that our SOC2 Type II audit trail is tamper-evident.',
        gherkin: `Scenario: Privilege escalation logging
Given an admin user updating permission sets for any member
When role changes are committed
Then write event to append-only tamper-evident audit log with actor ID, IP, and timestamp
And replicate log to S3 Glacier for SOC2 Type II compliance`
      }
    ],
    prfaq: `# AMAZON-STYLE PRFAQ: Zero-Trust Enterprise Identity & SAML Gateway
FOR IMMEDIATE RELEASE: Ascona AI Launches Okta SCIM & Multi-Tenant RBAC Suite

SAN FRANCISCO — Ascona AI today announced automated SCIM 2.0 identity lifecycle provisioning and cryptographic audit logging for Fortune 500 security compliance.

Customer Problem:
Enterprise IT departments require automated employee provisioning and zero-trust role enforcement before approving any software in their vendor procurement cycle.

Solution & Architecture:
Support Okta, Azure AD, and Google Workspace SSO with real-time SCIM webhook synchronization. Database partitioning enforces Postgres Row-Level Security (RLS) on every transaction.

Internal Engineering FAQ:
Q1: How do we prevent cross-tenant data leakage?
A: Connection pool policies enforce session tenant_id injection before executing any SQL statement.
Q2: What is the audit log retention period?
A: Audit events are cryptographically hashed and mirrored to write-once Glacier storage for 7 years.`,
    sprintPlan: [
      {
        "Story": "Sprint I: SCIM 2.0 Directory Sync",
        "Expected Tasks": "OAuth token mapping, SCIM user webhook handlers, group bindings",
        "Resource Name": "Security Architect",
        "Sprint ID": "SPR-01",
        "Plan Start Date": "2026-10-14",
        "Actual Start Date": "2026-10-14",
        "Plan End Date": "2026-10-26",
        "Actual End Date": "2026-10-26",
        "Ticket ID": "ASC-301",
        "Estimate (in Days)": 8,
        "Deliverable": "SCIM Provisioning Service",
        "Status": "In Progress",
        "Artifacts": "Okta Developer Verified Cert",
        "Adjusted Estimate": 8,
        "Actual Effort (Days)": 7,
        "Comments": "Automated account provisioning under 5s"
      },
      {
        "Story": "Sprint II: Row-Level Tenant Security",
        "Expected Tasks": "Postgres RLS migration, session variable injection in Prisma",
        "Resource Name": "Database Architect",
        "Sprint ID": "SPR-01",
        "Plan Start Date": "2026-10-27",
        "Actual Start Date": "2026-10-27",
        "Plan End Date": "2026-11-06",
        "Actual End Date": "2026-11-06",
        "Ticket ID": "ASC-302",
        "Estimate (in Days)": 5,
        "Deliverable": "RLS Partition Policy",
        "Status": "Ready for Review",
        "Artifacts": "Multi-tenant penetration test report",
        "Adjusted Estimate": 5,
        "Actual Effort (Days)": 5,
        "Comments": "Zero cross-tenant leakage guaranteed"
      },
      {
        "Story": "Sprint III: Tamper-Evident Audit Logs",
        "Expected Tasks": "SHA-256 hash chaining, S3 Glacier archival pipeline",
        "Resource Name": "DevOps Engineer",
        "Sprint ID": "SPR-02",
        "Plan Start Date": "2026-11-07",
        "Actual Start Date": "2026-11-07",
        "Plan End Date": "2026-11-18",
        "Actual End Date": "2026-11-18",
        "Ticket ID": "ASC-303",
        "Estimate (in Days)": 3,
        "Deliverable": "SOC2 Audit Pipeline",
        "Status": "Backlog",
        "Artifacts": "Audit compliance ledger",
        "Adjusted Estimate": 3,
        "Actual Effort (Days)": 3,
        "Comments": "Read-only audit trail with tamper alarm"
      }
    ],
    cards: [
      { name: "Okta SAML 2.0 & SCIM Sync", text: "Automated identity provisioning and organization group role bindings.", points: 8 },
      { name: "Postgres Row-Level Security Isolation", text: "Enforce tenant_id query isolation at database connection pool level.", points: 5 },
      { name: "Cryptographic Security Audit Ledger", text: "Append-only audit trail replicated to Glacier for SOC2 Type II compliance.", points: 3 }
    ]
  }
];

// Product Showcase Tabs
const SHOWCASE_TABS = [
  {
    id: 'decomposition',
    title: '1. Intelligent Decomposition',
    subtitle: 'From Unstructured Spec to Structured Jira Hierarchy',
    description: 'Paste any PRD, product brief, or technical RFC. Ascona’s fine-tuned model identifies core architectural domains, breaking them down into Epics, Stories, and Subtasks with zero human grooming required.',
    bullets: [
      'Identifies domain boundaries, edge cases, and hidden dependencies',
      'Preserves original intent while structuring into Agile best practices',
      'Generates consistent technical naming conventions across services'
    ],
    code: `// Ascona AI Spec Parser v2.4
[ANALYSIS_PIPELINE] Input: 4-page Notion PRD (Checkout v2)
├── Detected Domains: 3 (Payments, Auth, Webhooks)
├── Extracted Epics: 1 (EPIC-411)
├── Generated Stories: 4
└── Subtasks: 12 (Frontend, Backend, DevOps)
Status: Ready for Jira Cloud Export [28.4s]`
  },
  {
    id: 'gherkin',
    title: '2. Gherkin Acceptance Criteria',
    subtitle: 'Testable Given-When-Then Scenarios on Every Story',
    description: 'Vague acceptance criteria lead to sprint delays and buggy releases. Ascona automatically drafts rigorous Given-When-Then test cases that QA engineers can copy directly into Playwright, Cypress, or Cucumber.',
    bullets: [
      'Standardized Given-When-Then format for bulletproof QA testing',
      'Covers happy path, timeout edge cases, and unauthorized states',
      'Directly compatible with automated BDD testing suites'
    ],
    code: `Feature: Payment Method Fallback
  Scenario: Card expired during subscription renewal
    Given customer has an expired default credit card
    And a valid secondary backup card on file
    When Stripe initiates billing invoice renewal
    Then automatically attempt charge against secondary card
    And notify customer of primary card expiration via Slack/Email`
  },
  {
    id: 'estimation',
    title: '3. Velocity Calibration',
    subtitle: 'Fibonacci Story Points Based on Real Complexity',
    description: 'Planning poker wastes hours of engineering time. Ascona evaluates historical commit patterns, architectural scope, and external API dependencies to recommend accurate story point allocations.',
    bullets: [
      'Calibrated to Fibonacci scale (1, 2, 3, 5, 8, 13)',
      'Accounts for external service integration risks and database migrations',
      'Learns your team’s velocity trends sprint over sprint'
    ],
    code: `[ESTIMATION_ENGINE] ASC-202 Calibration:
├── DB Schema Migration: Moderate (+2 pts)
├── Stripe Idempotency Logic: High (+3 pts)
├── Unit Test Coverage Requirement: (+2 pts)
├── Risk Multiplier: 1.15
└── FINAL STORY POINTS: 8 pts (Confidence: 94.2%)`
  },
  {
    id: 'sync',
    title: '4. Two-Way Atlassian Sync',
    subtitle: 'One-Click Publish to Jira Cloud & Confluence',
    description: 'No more copy-pasting between chat tools and Jira. Ascona integrates directly with Atlassian REST APIs, creating issues, setting parent-child relationships, assigning sprint tags, and updating Confluence specs.',
    bullets: [
      'Zero manual data entry: push entire epics in under 2 seconds',
      'Supports Jira Cloud, Jira Data Center, and Linear workspaces',
      'Bi-directional sync preserves custom fields and workflow statuses'
    ],
    code: `[ATLASSIAN_CLOUD_GATEWAY]
POST https://api.atlassian.com/ex/jira/v3/issue/bulk
Response: 201 Created (Duration: 840ms)
├── Created EPIC: PROJ-912 (Metered Billing)
├── Created Stories: PROJ-913, PROJ-914, PROJ-915
└── Linked to Confluence Space: ENGINEERING/PRD-402`
  }
];

// Dynamic Ascona Decomposition Engine (Reusing and extending Desktop/ascona and Desktop/ascona-backend schemas)
function generateCompleteAsconaArtifacts(promptText, detailLevel = 'detailed') {
  const clean = (promptText || '').trim();
  const summaryTitle = clean.length > 55 ? clean.substring(0, 52) + '...' : (clean || 'Enterprise Service Architecture');

  // Generate randomized ticket prefix seed to ensure distinct IDs on every button click
  const seed = 300 + Math.floor(Math.random() * 600);
  const epicKey = `EPIC-${400 + (seed % 90)}`;
  const ticket1 = `ASC-${seed}`;
  const ticket2 = `ASC-${seed + 1}`;
  const ticket3 = `ASC-${seed + 2}`;
  const ticket4 = `ASC-${seed + 3}`;
  // DYNAMIC DOMAIN GENERATOR: Synthesizes bespoke stories and criteria tailored to user prompt!
  const rawClauses = clean
    .split(/[,;.]|\band\b|\bwith\b|\bincluding\b|\bas well as\b/i)
    .map(c => c.trim().replace(/^(build|create|implement|develop|design|add|support|provide|a|an|the)\s+/i, ''))
    .filter(c => c.length > 4);

  const f1 = rawClauses[0] || `${clean.slice(0, 32)} Core Pipeline`;
  const f2 = rawClauses[1] || `${clean.slice(0, 32)} Processing Service`;
  const f3 = rawClauses[2] || `${clean.slice(0, 32)} State Resilience & Sync`;
  const f4 = rawClauses[3] || `${clean.slice(0, 32)} Automated QA & Security`;

  const capF1 = f1.charAt(0).toUpperCase() + f1.slice(1);
  const capF2 = f2.charAt(0).toUpperCase() + f2.slice(1);
  const capF3 = f3.charAt(0).toUpperCase() + f3.slice(1);
  const capF4 = f4.charAt(0).toUpperCase() + f4.slice(1);

  return {
    epic: {
      key: epicKey,
      title: `Initiative Architecture: ${summaryTitle}`,
      description: `Autonomous end-to-end technical decomposition for: "${clean}". Outlines core service architecture, transactional data models, resilient API boundaries, and production deployment guardrails.`
    },
    stories: [
      {
        key: ticket1,
        title: `${capF1} Implementation`,
        points: 5,
        storyPoints: 5,
        sprint: 1,
        labels: ['architecture', 'backend', 'p0'],
        prerequisites: [],
        type: 'Story',
        description: `As an end-user, I want ${f1.toLowerCase()}, so that foundational domain capabilities are initialized.`,
        gherkin: `Scenario: Base initialization of ${f1.toLowerCase()}
Given configured system runtime parameters
When ${f1.toLowerCase()} executes
Then verify data schema migrations and allocate connection resources
And emit an active telemetry heartbeat to Datadog`
      },
      {
        key: ticket2,
        title: `${capF2} Pipeline`,
        points: 8,
        storyPoints: 8,
        sprint: 1,
        labels: ['api', 'processing', 'p0'],
        prerequisites: [ticket1],
        type: 'Story',
        description: `As a software engineer, I want ${f2.toLowerCase()}, so that high-throughput transactions execute reliably.`,
        gherkin: `Scenario: High-concurrency execution for ${f2.toLowerCase()}
Given incoming workload batches
When service invokes ${f2.toLowerCase()}
Then process requests with sub-150ms P99 latency
And handle retry schedules on transient upstream network disruptions`
      },
      {
        key: ticket3,
        title: `${capF3} Architecture`,
        points: 5,
        storyPoints: 5,
        sprint: 2,
        labels: ['resilience', 'devops', 'p1'],
        prerequisites: [ticket1],
        type: 'Story',
        description: `As a Site Reliability Engineer, I want ${f3.toLowerCase()}, so that system availability maintains a 99.99% uptime SLA.`,
        gherkin: `Scenario: Failover and recovery sequence
Given unexpected socket closure or node degradation
When health monitor triggers recovery
Then maintain operational state for ${f3.toLowerCase()}
And alert operations team via incident telemetry hook`
      },
      {
        key: ticket4,
        title: `${capF4} & Automated QA`,
        points: 3,
        storyPoints: 3,
        sprint: 2,
        labels: ['security', 'compliance', 'qa'],
        prerequisites: [ticket2, ticket3],
        type: 'Story',
        description: `As a security compliance officer, I want ${f4.toLowerCase()}, so that SOC2 and ISO-27001 standards are certified.`,
        gherkin: `Scenario: Compliance and regression testing
Given a candidate release build for ${f4.toLowerCase()}
When automated CI regression test suites execute
Then verify 100% of acceptance criteria pass
And commit cryptographically signed audit logs for compliance review`
      }
    ],
    prfaq: `# AMAZON-STYLE WORKING BACKWARDS PRFAQ: ${summaryTitle}
FOR IMMEDIATE RELEASE: Ascona AI Announces Production-Ready Architecture for ${summaryTitle}

SAN FRANCISCO — Ascona AI has decomposed "${clean}" into production-grade Agile artifacts, accelerating implementation velocity by 10x.

Customer Problem & Industry Friction:
Engineering teams lose weeks drafting unstructured technical specifications, debating story point weights, and manually writing acceptance criteria, delaying quarterly product releases.

Solution & Technical Architecture:
Ascona AI decomposes "${clean}" into decoupled domain services:
1. ${capF1}
2. ${capF2}
3. ${capF3}
4. ${capF4}

Customer Testimonial:
"Ascona converted our high-level architectural requirements into actionable Jira issues and sprint matrices in seconds. Grooming meetings went from 3 hours to 15 minutes." — VP of Engineering.

Internal Engineering FAQ:
Q1: What are the primary dependencies for this system?
A: Ingestion schema migrations, external API wrappers, and cryptographic audit log pipelines.
Q2: What is the rollback and disaster recovery procedure?
A: Every state modification is idempotent with automated canary rollback guardrails.
Q3: How are compliance, auditability, and data security validated?
A: System state transitions are verified via Given-When-Then test cases and hashed audit trails.
Q4: What are the P99 performance targets?
A: Core query execution is constrained under 120ms with Redis distributed caching.`,
    sprintPlan: [
      {
        "Story": `Sprint I: ${capF1}`,
        "Expected Tasks": "DB migration, connection pooling, core domain entities",
        "Resource Name": "Backend Architect",
        "Sprint ID": "SPR-01",
        "Plan Start Date": "2026-10-15",
        "Actual Start Date": "2026-10-15",
        "Plan End Date": "2026-10-25",
        "Actual End Date": "2026-10-25",
        "Ticket ID": ticket1,
        "Estimate (in Days)": 5,
        "Deliverable": capF1,
        "Status": "In Progress",
        "Artifacts": "OpenAPI Spec & Migrations",
        "Adjusted Estimate": 5,
        "Actual Effort (Days)": 4,
        "Comments": "Core domain models validated"
      },
      {
        "Story": `Sprint II: ${capF2}`,
        "Expected Tasks": "Service orchestration, rate limiting, retry queue",
        "Resource Name": "Senior Software Eng",
        "Sprint ID": "SPR-02",
        "Plan Start Date": "2026-10-26",
        "Actual Start Date": "2026-10-26",
        "Plan End Date": "2026-11-06",
        "Actual End Date": "2026-11-06",
        "Ticket ID": ticket2,
        "Estimate (in Days)": 8,
        "Deliverable": capF2,
        "Status": "Ready for Review",
        "Artifacts": "Load test benchmarks",
        "Adjusted Estimate": 8,
        "Actual Effort (Days)": 7,
        "Comments": "All edge cases covered"
      },
      {
        "Story": `Sprint III: ${capF3}`,
        "Expected Tasks": "Failover circuit breaker, Redis cache, queue worker",
        "Resource Name": "Platform Engineer",
        "Sprint ID": "SPR-03",
        "Plan Start Date": "2026-11-07",
        "Actual Start Date": "2026-11-07",
        "Plan End Date": "2026-11-18",
        "Actual End Date": "2026-11-18",
        "Ticket ID": ticket3,
        "Estimate (in Days)": 5,
        "Deliverable": capF3,
        "Status": "Backlog",
        "Artifacts": "Chaos Engineering Suite",
        "Adjusted Estimate": 5,
        "Actual Effort (Days)": 5,
        "Comments": "Automatic failover tested"
      },
      {
        "Story": `Sprint IV: ${capF4}`,
        "Expected Tasks": "Audit trail logging, Playwright test suite, canary deploy",
        "Resource Name": "QA / DevOps Lead",
        "Sprint ID": "SPR-04",
        "Plan Start Date": "2026-11-19",
        "Actual Start Date": "2026-11-19",
        "Plan End Date": "2026-11-30",
        "Actual End Date": "2026-11-30",
        "Ticket ID": ticket4,
        "Estimate (in Days)": 3,
        "Deliverable": capF4,
        "Status": "Backlog",
        "Artifacts": "Security audit certification",
        "Adjusted Estimate": 3,
        "Actual Effort (Days)": 3,
        "Comments": "Ready for sprint release"
      }
    ],
    cards: [
      { name: capF1, text: `Foundational implementation and service contract initialization for ${f1}.`, points: 5, ticketId: ticket1 },
      { name: capF2, text: `High-throughput processing pipeline and transaction reliability for ${f2}.`, points: 8, ticketId: ticket2 },
      { name: capF3, text: `Resilience architecture, node failover and state recovery for ${f3}.`, points: 5, ticketId: ticket3 },
      { name: capF4, text: `Automated testing suite and cryptographic audit compliance for ${f4}.`, points: 3, ticketId: ticket4 }
    ]
  };
}

// Transform real backend response (from ascona-backend /inference/backlog & /inference/sprint-plan) into full interactive artifacts
function transformBackendEpicsToArtifacts(cloudEpics, customPrompt, detailLevel = 'detailed') {
  if (!cloudEpics || !Array.isArray(cloudEpics) || cloudEpics.length === 0) {
    return generateCompleteAsconaArtifacts(customPrompt, detailLevel);
  }

  const clean = (customPrompt || '').trim();
  const epicData = cloudEpics[0] || {};
  const seed = 400 + Math.floor(Math.random() * 500);
  const epicKey = `EPIC-${(seed % 90) + 400}`;

  const rawStories = cloudEpics.flatMap((epic, eIdx) => 
    (epic.userStories || []).map(s => ({
      ...s,
      epicTitle: epic.title || `Epic ${eIdx + 1}`
    }))
  );

  if (rawStories.length === 0) {
    return generateCompleteAsconaArtifacts(customPrompt, detailLevel);
  }

  const epicTitle = cloudEpics.length > 1
    ? `Initiative: ${clean.substring(0, 48)} (${cloudEpics.length} Epics)`
    : (epicData.title || `Initiative Architecture: ${clean.substring(0, 45)}`);
  const epicDesc = epicData.description || `Autonomous decomposition for: "${clean}". Outlines core service architecture, transactional data models, resilient API boundaries, and production deployment guardrails.`;

  const stories = rawStories.map((s, idx) => {
    const key = `ASC-${seed + idx}`;
    const pts = typeof s.storyPoints === 'number' && s.storyPoints > 0 ? s.storyPoints : (idx % 2 === 0 ? 5 : 3);
    const sprintNum = s.sprint || (idx < Math.ceil(rawStories.length / 2) ? 1 : 2);
    
    let gherkinText = s.acceptanceCriteria || '';
    if (!gherkinText.includes('Scenario:') && !gherkinText.includes('Given')) {
      gherkinText = `Scenario: Automated verification of ${s.title}
Given standard operational runtime environment
When action for "${s.title}" is executed
Then verify state consistency and emit compliance audit logs`;
    }

    return {
      key,
      title: s.title || `Story ${idx + 1}`,
      points: pts,
      storyPoints: pts,
      sprint: sprintNum,
      labels: ['backend-api', 'ai-generated', idx === 0 ? 'p0' : 'p1'],
      prerequisites: (s.prerequisites || []).map(p => `ASC-${seed + (typeof p === 'number' ? p - 1 : 0)}`),
      type: 'Story',
      description: s.description || `As an engineering user, I want ${s.title}, so that business capabilities are delivered.`,
      gherkin: gherkinText
    };
  });

  const sprintPlan = stories.map((s, idx) => {
    const sprintNum = s.sprint || (idx < Math.ceil(stories.length / 2) ? 1 : 2);
    const sprintId = `SPR-0${sprintNum}`;
    const roles = ['Principal Architect', 'Senior Backend Eng', 'Platform Engineer', 'QA / SDET Lead', 'Frontend Lead'];
    const resource = roles[idx % roles.length];
    const days = Math.max(1, Math.round(s.points * 0.7));
    const status = idx === 0 ? 'In Progress' : (idx === 1 ? 'Ready for Review' : 'Backlog');

    return {
      "Story": s.title,
      "Expected Tasks": s.description ? s.description.substring(0, 90) : `Implementation tasks for ${s.title}`,
      "Resource Name": resource,
      "Sprint ID": sprintId,
      "Plan Start Date": sprintNum === 1 ? '2026-10-15' : '2026-10-28',
      "Actual Start Date": sprintNum === 1 ? '2026-10-15' : '2026-10-28',
      "Plan End Date": sprintNum === 1 ? '2026-10-27' : '2026-11-10',
      "Actual End Date": sprintNum === 1 ? '2026-10-27' : '2026-11-10',
      "Ticket ID": s.key,
      "Estimate (in Days)": days,
      "Deliverable": s.title,
      "Status": status,
      "Artifacts": idx % 2 === 0 ? "OpenAPI Spec & Migrations" : "Integration Tests & CI Guardrails",
      "Adjusted Estimate": days,
      "Actual Effort (Days)": Math.max(1, days - 1),
      "Comments": s.epicTitle ? `Part of ${s.epicTitle}` : "Calibrated by Ascona Sprint Planner"
    };
  });

  const prfaq = `Amazon PRFAQ: ${epicTitle}
Status: RFC Production Ready | Release Target: Q4 2026
Operational Methodology: Amazon Working Backwards (6-Pager Rigor)

1. INTERNAL PRESS RELEASE (SEATTLE, WA)
Engineering leadership announced the immediate delivery plan for ${epicTitle}. By decomposing architecture specs directly into actionable Jira Epics, squads align technical milestones with core customer value propositions.

Customer Problem & Executive Summary:
${epicDesc}

Customer Quote:
"Ascona autonomously generated ${stories.length} verified user stories across ${cloudEpics.length} epics with full Given-When-Then acceptance criteria in seconds, eliminating sprint refinement ambiguity and reclaiming 4.2 hours per engineer per sprint."

2. CUSTOMER & STAKEHOLDER FAQ
Q1: What specific capabilities are delivered in this milestone?
A1: Decomposes into ${stories.length} technical deliverables covering: ${stories.slice(0, 4).map(s => s.title).join('; ')}.

Q2: How are story points calibrated across squads?
A2: Fibonacci sizing (1, 2, 3, 5, 8, 13, 21) calibrated against schema migrations, third-party API dependencies, and velocity modeling.

3. TECHNICAL ARCHITECTURE & RISK FAQ
Q3: What are the primary architectural boundaries?
A3: ${cloudEpics.map((e, idx) => `Epic ${idx + 1} (${e.title || 'Service Subsystem'}): ${e.description || 'Core domain logic'}`).join('; ')}

Q4: How is QA verification automated?
A4: All user stories include standardized Gherkin BDD scenarios directly copyable into automated Playwright, Cypress, and Cucumber test suites.`;

  const cards = stories.map(s => ({
    name: s.title,
    text: s.description ? s.description.substring(0, 110) + '...' : `Implementation story for ${s.title}`,
    points: s.points,
    ticketId: s.key
  }));

  return {
    epic: {
      key: epicKey,
      title: epicTitle,
      description: epicDesc
    },
    stories,
    sprintPlan,
    prfaq,
    cards
  };
}

function App() {
  // Navigation Scroll Spy & Mobile Drawer State
  const [activeSection, setActiveSection] = useState('top');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Simulator State (Dual-Engine from Desktop/ascona)
  const [activePresetIndex, setActivePresetIndex] = useState(0);
  const [customPrompt, setCustomPrompt] = useState(SIMULATOR_PRESETS[0].prompt);
  const [detailLevel, setDetailLevel] = useState('detailed'); // 'detailed' | 'medium' | 'high-level'
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState('');
  const [generationMetadata, setGenerationMetadata] = useState(null);
  const [justUpdated, setJustUpdated] = useState(false);
  const [activeArtifacts, setActiveArtifacts] = useState(SIMULATOR_PRESETS[0]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [copiedPrfaq, setCopiedPrfaq] = useState(false);
  const [createdCardIndex, setCreatedCardIndex] = useState(null);
  const [activeSimulatorView, setActiveSimulatorView] = useState('backlog'); // 'backlog' | 'sprint-plan' | 'prfaq' | 'cards'

  // Live Backend Connection Configuration State (from Desktop/ascona & ascona-backend)
  const [backendUrl, setBackendUrl] = useState(() => {
    try {
      const stored = localStorage.getItem('ascona_backend_url');
      if (!stored || stored.includes('app.ascona.ai')) {
        return '/api';
      }
      return stored;
    } catch (e) {
      return '/api';
    }
  });
  const [backendAuthToken, setBackendAuthToken] = useState(() => {
    try {
      return localStorage.getItem('ascona_auth_token') || '';
    } catch (e) {
      return '';
    }
  });
  const [showBackendConfig, setShowBackendConfig] = useState(false);
  const [pingStatus, setPingStatus] = useState(null);
  const [isPinging, setIsPinging] = useState(false);

  // Showcase Tabs State
  const [activeShowcaseTab, setActiveShowcaseTab] = useState('decomposition');

  // ROI Calculator State
  const [teamSize, setTeamSize] = useState(24);
  const [avgSalary, setAvgSalary] = useState(165000);
  const [groomingHoursPerWeek, setGroomingHoursPerWeek] = useState(3);

  // FAQ Accordion State
  const [openFaq, setOpenFaq] = useState(0);

  // Pricing & Commercial Billing Cycle State ('annual' | 'monthly')
  const [billingCycle, setBillingCycle] = useState('annual');

  // Product Tour Tab State ('jira-sync' | 'prd-prfaq' | 'sprint-matrix' | 'mcp-cli')
  const [activeTourTab, setActiveTourTab] = useState('jira-sync');

  // Institutional Diligence & Defensibility Hub Tab State ('moat' | 'proof' | 'governance')
  const [activeDiligenceTab, setActiveDiligenceTab] = useState('moat');

  // Enterprise Pilot Signup State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: 'Product Manager',
    teamSize: '10-50',
    currentTool: 'Jira Cloud'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Lightweight client-side router for /demo live production demo
  const [currentRoute, setCurrentRoute] = useState(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/+$/, '');
      if (path === '/demo') return '/demo';
    }
    return '/';
  });

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.replace(/\/+$/, '');
      if (path === '/demo') {
        setCurrentRoute('/demo');
      } else {
        setCurrentRoute('/');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = (path) => {
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', path);
      setCurrentRoute(path.replace(/\/+$/, '') || '/');
      window.scrollTo(0, 0);
    }
  };
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'platform-moat' || hash === 'team-pedigree' || hash === 'strategic-value') {
        setActiveDiligenceTab('moat');
      } else if (hash === 'discovery-proof') {
        setActiveDiligenceTab('proof');
      } else if (hash === 'capital-allocation' || hash === 'security-governance') {
        setActiveDiligenceTab('governance');
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  // Scroll Spy Effect: Updates active nav item as user scrolls down the landing page
  useEffect(() => {
    const sections = [
      'top',
      'simulator',
      'product-tour',
      'cli-mcp',
      'how-it-works',
      'features',
      'github-comparison',
      'diligence',
      'roi',
      'comparison',
      'security',
      'pricing',
      'faq',
      'signup'
    ];

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 130;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll handler with auto-close for mobile menu and smart diligence tab routing
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    // If targeted id is one of the diligence sub-sections, activate that tab and scroll to hub
    if (id === 'platform-moat' || id === 'team-pedigree' || id === 'strategic-value') {
      setActiveDiligenceTab('moat');
      const el = document.getElementById('diligence');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setActiveSection('diligence');
      }
      return;
    }
    if (id === 'discovery-proof') {
      setActiveDiligenceTab('proof');
      const el = document.getElementById('diligence');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setActiveSection('diligence');
      }
      return;
    }
    if (id === 'capital-allocation' || id === 'security-governance') {
      setActiveDiligenceTab('governance');
      const el = document.getElementById('diligence');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setActiveSection('diligence');
      }
      return;
    }

    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  // Handle Preset Selection
  const handleSelectPreset = (index) => {
    setActivePresetIndex(index);
    setCustomPrompt(SIMULATOR_PRESETS[index].prompt);
    setActiveArtifacts(SIMULATOR_PRESETS[index]);
    setSelectedStoryIndex(0);
    setSyncStatus(null);
    setGenerationMetadata(null);
  };

  // Test live backend connection
  const handleTestBackendConnection = async () => {
    setIsPinging(true);
    setPingStatus('Probing backend endpoint...');
    let backendEndpoint = backendUrl.trim().replace(/\/+$/, '');
    if (!backendEndpoint) backendEndpoint = '/api';

    const endpointsToTry = [backendEndpoint];
    const directEndpoint = 'https://api-iukesuj52a-uc.a.run.app/api';
    if (backendEndpoint !== directEndpoint) {
      endpointsToTry.push(directEndpoint);
    }

    let connected = false;
    for (const ep of endpointsToTry) {
      try {
        const res = await fetch(`${ep}/inference/backlog`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
            ...(backendAuthToken.trim() ? { 'Authorization': `Bearer ${backendAuthToken.trim()}` } : {})
          },
          body: JSON.stringify({
            companyInfo: "Ping Test",
            epicInfo: "Ping Test",
            detailLevel: "high-level"
          })
        });
        const cType = res.headers.get('content-type') || '';
        if (res.ok && cType.includes('application/json')) {
          setPingStatus(`✓ Online: HTTP ${res.status} OK at ${ep} (Ready for live inference)`);
          connected = true;
          break;
        } else if (res.status === 401) {
          setPingStatus(`⚡ Reached Backend (${ep}): HTTP 401 (Live cloud API active)`);
          connected = true;
          break;
        }
      } catch (e) {
        // Continue to fallback
      }
    }

    if (!connected) {
      setPingStatus(`⚡ Local engine active (Direct cloud fallback ready on click)`);
    }
    setIsPinging(false);
  };

  const handleSaveBackendConfig = () => {
    try {
      localStorage.setItem('ascona_backend_url', backendUrl.trim());
      localStorage.setItem('ascona_auth_token', backendAuthToken.trim());
      setShowBackendConfig(false);
    } catch (e) {
      setShowBackendConfig(false);
    }
  };

  // Trigger Live Backlog Generation (calling real backend and fallback dual-engine)
  const handleTriggerGenerate = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setSyncStatus(null);
    setJustUpdated(false);

    let configuredEndpoint = backendUrl.trim().replace(/\/+$/, '');
    if (!configuredEndpoint) configuredEndpoint = '/api';

    // Prioritized list of endpoints:
    // 1. Configured endpoint (relative /api on hosting domain or localhost)
    // 2. Direct Cloud Run backend URL
    const endpointsToTry = [configuredEndpoint];
    const directEndpoint = 'https://api-iukesuj52a-uc.a.run.app/api';
    if (configuredEndpoint !== directEndpoint) {
      endpointsToTry.push(directEndpoint);
    }

    setGenerationStep(`⚡ [1/4] Contacting Ascona AI Engine & Mistral LLM (${endpointsToTry[0]}/inference/backlog)...`);

    let finalArtifacts = null;
    let backendInfo = null;

    const progressTimer = setInterval(() => {
      setGenerationStep(prev => {
        if (!prev || prev.includes('[1/4]')) return '⚡ [2/4] Decomposing requirements with Mistral LLM into Epics & Stories...';
        if (prev.includes('[2/4]')) return '⚡ [3/4] Synthesizing Gherkin acceptance criteria & calculating Fibonacci story points...';
        if (prev.includes('[3/4]')) return '⚡ [4/4] Generating Sprint Plan & calibrating velocity capacity...';
        return prev;
      });
    }, 2800);

    try {
      for (let i = 0; i < endpointsToTry.length; i++) {
        const activeEndpoint = endpointsToTry[i];
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 26000);

          const headers = { 'Content-Type': 'application/json' };
          if (backendAuthToken.trim()) {
            headers['Authorization'] = `Bearer ${backendAuthToken.trim()}`;
          }

          const backlogPayload = {
            companyInfo: "Enterprise Cloud Platforms & B2B SaaS",
            epicInfo: customPrompt,
            detailLevel: detailLevel,
            additionalInfo: "Decomposed for Jira Cloud, sprint capacity planning, and automated QA acceptance testing."
          };

          const response = await fetch(`${activeEndpoint}/inference/backlog`, {
            method: "POST",
            headers,
            body: JSON.stringify(backlogPayload),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          const contentType = response.headers.get('content-type') || '';
          if (response.ok && contentType.includes('application/json')) {
            setGenerationStep('⚡ [3/4] Received dynamic Epics from Backend! Calling Sprint Planner (/inference/sprint-plan)...');
            const cloudEpics = await response.json();

            if (Array.isArray(cloudEpics) && cloudEpics.length > 0) {
              try {
                const planController = new AbortController();
                const planTimeout = setTimeout(() => planController.abort(), 12000);
                const planRes = await fetch(`${activeEndpoint}/inference/sprint-plan`, {
                  method: "POST",
                  headers,
                  body: JSON.stringify({
                    epics: cloudEpics,
                    velocity: 20,
                    sprintLength: 2
                  }),
                  signal: planController.signal
                });
                clearTimeout(planTimeout);

                const planContentType = planRes.headers.get('content-type') || '';
                if (planRes.ok && planContentType.includes('application/json')) {
                  const plannedEpics = await planRes.json();
                  finalArtifacts = transformBackendEpicsToArtifacts(plannedEpics, customPrompt, detailLevel);
                  backendInfo = `Live Ascona Backend (${activeEndpoint}) [HTTP 200 OK + Sprint Plan]`;
                  break;
                }
              } catch (planErr) {
                // Sprint plan error, cloudEpics will be transformed below
              }

              finalArtifacts = transformBackendEpicsToArtifacts(cloudEpics, customPrompt, detailLevel);
              backendInfo = `Live Ascona Backend (${activeEndpoint}) [HTTP 200 OK]`;
              break;
            }
          }
        } catch (endpointErr) {
          // Try fallback endpoint
        }
      }
    } finally {
      clearInterval(progressTimer);
    }

    setGenerationStep('⚡ [4/4] Calibrating 16-field Agile Sprint Matrix & Board cards...');
    await new Promise(r => setTimeout(r, 260));

    if (!finalArtifacts) {
      finalArtifacts = generateCompleteAsconaArtifacts(customPrompt, detailLevel);
    }

    setActiveArtifacts(finalArtifacts);
    setSelectedStoryIndex(0);
    setIsGenerating(false);
    setJustUpdated(true);

    const totalPts = finalArtifacts.stories.reduce((acc, s) => acc + (s.points || 0), 0);
    const titleSummary = finalArtifacts.epic.title
      .replace('Architecture & Implementation: ', '')
      .replace('Low-Latency WebRTC Voice Pipeline & PII Guardrails: ', '')
      .replace('Metered Subscription Engine & Dunning Workflows: ', '');

    setGenerationMetadata({
      title: titleSummary.length > 40 ? titleSummary.substring(0, 38) + '...' : titleSummary,
      storiesCount: finalArtifacts.stories.length,
      totalPoints: totalPts,
      sprintsCount: finalArtifacts.sprintPlan.length,
      backendInfo: backendInfo,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    });

    setTimeout(() => {
      setJustUpdated(false);
    }, 4000);
  };

  // Simulate Jira Sync
  const handleSimulateSync = () => {
    setSyncStatus('syncing');
    setTimeout(() => {
      setSyncStatus('synced');
    }, 1100);
  };

  // Create Card interaction (from Desktop/ascona create-trello-card endpoint)
  const handleCreateCard = (card, index) => {
    setCreatedCardIndex(index);
    setTimeout(() => setCreatedCardIndex(null), 2500);
  };

  // Copy Gherkin snippet to clipboard
  const handleCopyGherkin = (gherkinText, key) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(gherkinText);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  // Copy PRFAQ document to clipboard
  const handleCopyPrfaq = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedPrfaq(true);
      setTimeout(() => setCopiedPrfaq(false), 2000);
    }
  };

  // Export Sprint Plan as CSV
  const handleExportSprintPlanCsv = (plan) => {
    if (!plan || plan.length === 0) return;
    const headers = Object.keys(plan[0]);
    const rows = plan.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','));
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'ascona_sprint_plan.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Form Field Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit to Firebase
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const dbRef = ref(database, 'signups');
    const submissionPayload = {
      ...formData,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    push(dbRef, submissionPayload)
      .then(() => {
        setSubmitted(true);
        setIsSubmitting(false);
      })
      .catch((error) => {
        console.error('Firebase submission error:', error);
        setSubmitError('Unable to connect to the pilot server. Please try again.');
        setIsSubmitting(false);
      });
  };

  // Calculations for ROI
  const hoursSavedPerYear = teamSize * groomingHoursPerWeek * 48;
  const hourlyRate = avgSalary / 2000;
  const annualDollarsSaved = Math.round(hoursSavedPerYear * hourlyRate);

  // Active Data Payload (either custom decomposed artifacts or preset)
  const currentActiveData = activeArtifacts || SIMULATOR_PRESETS[activePresetIndex];
  const activeEpic = currentActiveData.epic;
  const activeStories = currentActiveData.stories;
  const activeSprintPlan = currentActiveData.sprintPlan;
  const activePrfaq = currentActiveData.prfaq;
  const activeCards = currentActiveData.cards;
  const activeShowcase = SHOWCASE_TABS.find(t => t.id === activeShowcaseTab) || SHOWCASE_TABS[0];

  // Route to 60-Second Live Production Demo if on /demo
  if (currentRoute === '/demo') {
    return <LiveDemo onNavigate={navigateTo} />;
  }

  return (
    <div className="ascona-app">
      <div className="bg-grid-pattern"></div>

      {/* Top Customer Announcement Bar */}
      <div className="top-announcement">
        <span className="announcement-badge">⚡ Control Plane v3.1</span>
        The Spec-to-PR Control Plane for Atlassian Jira &amp; GitHub · Multi-Repo AST &amp; CI Contract Gates
        <a href="#simulator" onClick={scrollTo('simulator')} className="announcement-link">Try Live Simulator ↓</a>
      </div>

      {/* Navigation Bar */}
      <nav className="main-nav">
        <div className="nav-container">
          <a href="#top" onClick={scrollTo('top')} className="brand-logo">
            <div className="brand-icon">A</div>
            <span className="brand-text">Ascona AI</span>
            <span className="brand-badge">Control Plane v3.1</span>
          </a>

          {/* Desktop Nav Links (Streamlined Executive Header) */}
          <ul className="nav-links">
            <li>
              <a
                href="#simulator"
                onClick={scrollTo('simulator')}
                className={activeSection === 'simulator' ? 'active' : ''}
              >
                Simulator
              </a>
            </li>
            <li>
              <a
                href="#product-tour"
                onClick={scrollTo('product-tour')}
                className={activeSection === 'product-tour' ? 'active' : ''}
              >
                Product Tour
              </a>
            </li>
            <li>
              <a
                href="#diligence"
                onClick={scrollTo('diligence')}
                className={activeSection === 'diligence' ? 'active' : ''}
              >
                Architecture &amp; Moat
              </a>
            </li>
            <li>
              <a
                href="#comparison"
                onClick={scrollTo('comparison')}
                className={activeSection === 'comparison' ? 'active' : ''}
              >
                Jira vs Linear
              </a>
            </li>
            <li>
              <a
                href="#pricing"
                onClick={scrollTo('pricing')}
                className={activeSection === 'pricing' ? 'active' : ''}
              >
                Pricing
              </a>
            </li>
            <li>
              <a href="/userGuide.html" target="_blank" rel="noopener noreferrer">
                📖 Docs ↗
              </a>
            </li>
          </ul>

          <div className="nav-actions">
            <a
              href="https://marketplace.atlassian.com/apps/3398389661/ascona-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-marketplace-btn"
            >
              🔷 Marketplace (v3.1) ↗
            </a>
            <a href="#signup" onClick={scrollTo('signup')} className="btn-primary-gradient btn-sm nav-cta-btn">
              Request Pilot →
            </a>
          </div>

          {/* Mobile Hamburger Toggle Button */}
          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile navigation menu"
          >
            <span className={`hamburger-bar ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-bar ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-bar ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>

        {/* Mobile Nav Drawer */}
        {mobileMenuOpen && (
          <div className="mobile-nav-drawer">
            <ul className="mobile-nav-list">
              <li>
                <a
                  href="#simulator"
                  className={activeSection === 'simulator' ? 'active' : ''}
                  onClick={scrollTo('simulator')}
                >
                  ⚡ Live Backlog Simulator
                </a>
              </li>
              <li>
                <a
                  href="#product-tour"
                  className={activeSection === 'product-tour' ? 'active' : ''}
                  onClick={scrollTo('product-tour')}
                >
                  📸 Product Tour &amp; Architecture
                </a>
              </li>
              <li>
                <a
                  href="#diligence"
                  className={activeSection === 'diligence' ? 'active' : ''}
                  onClick={scrollTo('diligence')}
                >
                  🏛️ Architecture &amp; Moat Hub
                </a>
              </li>
              <li>
                <a
                  href="#comparison"
                  className={activeSection === 'comparison' ? 'active' : ''}
                  onClick={scrollTo('comparison')}
                >
                  ⚔️ Jira vs Linear vs Ascona Control Plane
                </a>
              </li>
              <li>
                <a
                  href="#security"
                  className={activeSection === 'security' ? 'active' : ''}
                  onClick={scrollTo('security')}
                >
                  🔒 Bank-Grade Security &amp; ZDR
                </a>
              </li>
              <li>
                <a
                  href="#pricing"
                  className={activeSection === 'pricing' ? 'active' : ''}
                  onClick={scrollTo('pricing')}
                >
                  💳 Transparent Pricing ($6K Base / $27K Ent)
                </a>
              </li>
              <li>
                <a
                  href="https://marketplace.atlassian.com/apps/3398389661/ascona-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  🔷 Atlassian Marketplace (v3.1.0) ↗
                </a>
              </li>
              <li>
                <a
                  href="/userGuide.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  📖 Full User Guide &amp; API Docs ↗
                </a>
              </li>
            </ul>

            <div className="mobile-nav-cta">
              <a
                href="#signup"
                onClick={scrollTo('signup')}
                className="btn-primary-gradient btn-lg"
                style={{ width: '100%' }}
              >
                Request Pilot →
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header id="top" className="hero-section">
        <div className="hero-pill">
          <span className="hero-pill-dot"></span>
          <span>⚡ Enterprise Spec-to-PR Control Plane</span> · Atlassian Marketplace Partner (v3.1) · Verified GitHub Action
        </div>

        <h1 className="hero-title">
          The Spec-to-PR Control Plane <br />
          <span className="gradient-text">For Human Devs &amp; Coding Agents</span>
        </h1>

        <p className="hero-subtitle">
          Governing engineering scope and deterministic CI contract checks across Cursor, Claude Code, Jira, Linear, and GitHub. As coding agents make code generation near-free, 68% of wasted sprints stem from scope drift and broken multi-repo API contracts. Ascona indexes cross-repo ASTs, compiles specs into verified execution contracts, and blocks out-of-scope PRs in CI before code merges.
        </p>

        <div className="hero-ctas">
          <a href="#signup" onClick={scrollTo('signup')} className="btn-primary-gradient btn-lg">
            ⚡ Request Private Pilot (14-Day Trial) →
          </a>
          <a href="#simulator" onClick={scrollTo('simulator')} className="btn-secondary-dark btn-lg">
            Try Live Simulator ↓
          </a>
        </div>

        {/* Hero Split Visual: Multi-Repo Context & CI Gate on left, Live Jira / Linear Board on right */}
        <div className="hero-split-visual">
          <div className="hero-visual-pane terminal-pane">
            <div className="pane-header">
              <div className="pane-header-left">
                <div className="window-dots">
                  <span className="window-dot dot-red"></span>
                  <span className="window-dot dot-yellow"></span>
                  <span className="window-dot dot-green"></span>
                </div>
                <div className="pane-title">Multi-Repo AST &amp; Cursor/Claude MCP</div>
              </div>
              <span className="status-live">● Control Plane Live</span>
            </div>
            <div className="pane-content terminal-code">
              <div className="code-line prompt-line">
                <span className="prompt-symbol">$</span> npx @ascona/cli parse ./specs/auth-rbac.md --multi-repo --sync-jira --project SEC
              </div>
              <div className="code-line info-line">
                <span className="step-num">[1/3]</span> Indexing cross-repo AST graph across 14 microservices... <span className="step-ok">done [0.4s]</span>
              </div>
              <div className="code-line info-line">
                <span className="step-num">[2/3]</span> Synthesizing Epics &amp; Given-When-Then BDD contract tests... <span className="step-ok">done [0.8s]</span>
              </div>
              <div className="code-line info-line">
                <span className="step-num">[3/3]</span> Pre-Sprint Evaluator: 0 breaking schema hazards (89% recall)... <span className="step-ok">done [0.3s]</span>
              </div>
              <div className="code-line success-line">
                ✔ 200 OK — Synchronized to Jira Cloud (SEC-401..SEC-404) &amp; Linear
              </div>
              <div className="code-line success-line" style={{ color: '#38bdf8' }}>
                ✔ GitHub Actions Gate: @ascona/action active (98.2% CI precision)
              </div>
              <div className="mcp-config-hint">
                <code>@ascona/mcp-server</code> · Active tools: <code>ascona_decompose_prd</code>, <code>ascona_eval_contracts</code>
              </div>
            </div>
          </div>

          <div className="hero-visual-pane jira-pane">
            <div className="pane-header">
              <div className="pane-header-left">
                <div className="jira-logo-tag">🔷 Jira Cloud + Linear</div>
                <div className="pane-title">Project: SEC (Sprint 14) · 14 Repos Connected</div>
              </div>
              <span className="sync-badge">✓ Live Synced</span>
            </div>
            <div className="pane-content jira-board-mock">
              <div className="jira-epic-card">
                <div className="epic-meta">
                  <span className="epic-key">EPIC · SEC-401</span>
                  <span className="epic-pts">21 Pts Total</span>
                </div>
                <div className="epic-name">Multi-Tenant OAuth2 &amp; Session Invalidation Engine</div>
              </div>
              <div className="jira-ticket-row">
                <div className="ticket-info">
                  <span className="ticket-key">SEC-402</span>
                  <span className="ticket-title">PKCE flow with replay attack detection</span>
                </div>
                <div className="ticket-tags">
                  <span className="tag-pts">8 pts</span>
                  <span className="tag-status">Ready for Dev</span>
                </div>
              </div>
              <div className="jira-ticket-row">
                <div className="ticket-info">
                  <span className="ticket-key">SEC-403</span>
                  <span className="ticket-title">Zero-latency Redis session revocation worker</span>
                </div>
                <div className="ticket-tags">
                  <span className="tag-pts">5 pts</span>
                  <span className="tag-status">Ready for Dev</span>
                </div>
              </div>
              <div className="jira-ticket-row">
                <div className="ticket-info">
                  <span className="ticket-key">SEC-404</span>
                  <span className="ticket-title">Given-When-Then BDD test suite for Playwright</span>
                </div>
                <div className="ticket-tags">
                  <span className="tag-pts">5 pts</span>
                  <span className="tag-status">QA Ready</span>
                </div>
              </div>
              <div style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', fontSize: '0.8rem', color: '#4ade80', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span><strong>GitHub Actions Gate:</strong> <code>ascona-action</code></span>
                <span>✔ 98.2% Gate Precision · 0 Out-of-Scope Diffs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Atlassian Marketplace Trust & Vendor Support Strip */}
        <div className="marketplace-trust-ribbon">
          <a
            href="https://marketplace.atlassian.com/apps/3398389661/ascona-ai"
            target="_blank"
            rel="noopener noreferrer"
            className="marketplace-badge-pill"
          >
            🔷 Atlassian Marketplace App ID: <strong>3398389661</strong> (v3.1.0) ↗
          </a>
          <span className="marketplace-badge-pill support-badge">
            🛡️ Supported by Vendor · <strong>24h SLA</strong> (support@ascona.ai)
          </span>
          <span className="marketplace-badge-pill zdr-badge">
            🔒 Zero-Data Retention Guarantee (ZDR)
          </span>
          <span className="marketplace-badge-pill support-badge" style={{ color: '#38bdf8', borderColor: 'rgba(56, 189, 248, 0.3)' }}>
            ⚡ <strong>$28,500 MRR</strong> (+42.5% MoM) · 30 Paying Orgs
          </span>
        </div>

        {/* Enterprise Pedigree Banner */}
        <div className="founder-trust-strip">
          <span className="founder-trust-label">Engineered by distributed systems &amp; developer infrastructure veterans from</span>
          <div className="founder-company-badges">
            <span className="founder-pill amazon">📦 Amazon Working Backwards</span>
            <span className="founder-pill honey">🍯 Honey ($4B PayPal Acq)</span>
            <span className="founder-pill stringr">🎬 Distributed Video Ingestion</span>
            <span className="founder-pill caresend">🩺 Enterprise SOC 2 / HIPAA</span>
          </div>
        </div>

        {/* Audited 6-Spec Benchmark Metrics Banner */}
        <div className="metrics-banner">
          <div className="metric-item">
            <div className="metric-value">-64.2%</div>
            <div className="metric-label">Spec Planning Time (120m → 43m)</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">98.2%</div>
            <div className="metric-label">CI Contract Gate Precision</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">81%</div>
            <div className="metric-label">Agent PR Merge Rate (vs 46% baseline)</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">18.6x</div>
            <div className="metric-label">Verified ROI ($14K+/mo Saved / Team)</div>
          </div>
        </div>
      </header>

      {/* Integrations Ribbon */}
      <section className="integrations-strip">
        <div className="integrations-label">Native integrations with your existing engineering tools</div>
        <div className="logos-row">
          <span className="logo-badge">🐙 GitHub Issues & Projects (Auto-Populate)</span>
          <span className="logo-badge">🔷 Atlassian Jira Cloud</span>
          <span className="logo-badge">🏢 Jira Data Center</span>
          <span className="logo-badge">📖 Confluence</span>
          <span className="logo-badge">⚡ Linear</span>
          <span className="logo-badge">🎨 Figma Specs</span>
          <span className="logo-badge">💬 Slack Workflows</span>
        </div>
      </section>

      {/* Interactive Simulator Section (Reusing Desktop/ascona Dual-Engine) */}
      <section id="simulator" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Working Backlog Generator Live</span>
          <h2 className="section-title">Experience the Backlog Generator Live</h2>
          <p className="section-desc">
            No signup required. Select an architectural preset or type <strong>any custom product idea</strong> below.
            Watch Ascona generate Jira Epics, Given-When-Then Gherkin stories, Amazon-style PRFAQs, and full 16-point Sprint Plans.
          </p>
        </div>

        <div className="simulator-box">
          <div className="simulator-header">
            <div className="window-dots">
              <span className="window-dot dot-red"></span>
              <span className="window-dot dot-yellow"></span>
              <span className="window-dot dot-green"></span>
            </div>
            <div className="simulator-title-bar">
              <span>ascona-decomposition-engine · v2.4</span>
              <span className="status-live">● Dual-Engine Online</span>
            </div>
            <div>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Target: Atlassian Jira Cloud (Project ASCONA-ENG)</span>
            </div>
          </div>

          <div className="simulator-body">
            {/* Input Controls */}
            <div className="prompt-panel">
              <div className="panel-label">
                <span>1. Select Preset or Type Custom Idea</span>
                <span style={{ fontSize: '0.75rem', color: '#818cf8', textTransform: 'none' }}>Quick load</span>
              </div>
              <div className="presets-container">
                {SIMULATOR_PRESETS.map((preset, idx) => (
                  <button
                    key={preset.id}
                    onClick={() => handleSelectPreset(idx)}
                    className={`preset-chip ${activePresetIndex === idx ? 'active' : ''}`}
                  >
                    <span><strong>{preset.title}</strong></span>
                    <span style={{ fontSize: '0.72rem', opacity: 0.85, background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: '4px' }}>
                      {preset.tag}
                    </span>
                  </button>
                ))}
              </div>

              <div className="panel-label" style={{ marginTop: '10px' }}>
                <span>2. Specification / Feature Idea Input</span>
              </div>
              <textarea
                className="spec-textarea"
                rows="4"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Enter any project idea, feature specification, or architecture requirement..."
              />

              <div className="panel-label" style={{ marginTop: '12px' }}>
                <span>3. Decomposition Breakdown Level</span>
                <span style={{ fontSize: '0.75rem', color: '#818cf8', textTransform: 'none' }}>ai.service.ts</span>
              </div>
              <div className="detail-level-chips" style={{ marginBottom: '14px' }}>
                {[
                  { id: 'detailed', label: '🔬 Detailed (Subtasks & AC)' },
                  { id: 'medium', label: '⚡ Medium (Standard)' },
                  { id: 'high-level', label: '📌 High-Level (Epics)' }
                ].map((lvl) => (
                  <button
                    key={lvl.id}
                    type="button"
                    className={`detail-chip ${detailLevel === lvl.id ? 'active' : ''}`}
                    onClick={() => setDetailLevel(lvl.id)}
                  >
                    {lvl.label}
                  </button>
                ))}
              </div>

              <div className="simulator-generate-wrap">
                <button
                  type="button"
                  onClick={handleTriggerGenerate}
                  className="btn-primary-gradient simulator-generate-btn"
                  disabled={isGenerating}
                >
                  {isGenerating ? (generationStep || '⚡ Decomposing Requirements & Planning Sprints...') : '⚡ Generate Backlog & Sprint Plan'}
                </button>
              </div>

              <div className="simulator-subtext">
                Reuses Ascona’s dual-engine: Amazon PRFAQs, 16-point Agile Sprint Plans, Gherkin BDD criteria & Jira Cloud sync
              </div>
            </div>

            {/* Output Panel with Multi-View Tabs */}
            <div className={`output-panel ${justUpdated ? 'just-updated' : ''}`}>
              <div className="output-header">
                <div className="output-header-left">
                  <span className="jira-project-badge">Project: ASCONA-ENG</span>
                  <button
                    type="button"
                    className="backend-live-badge clickable"
                    onClick={() => setShowBackendConfig(!showBackendConfig)}
                    title="Click to view and configure live backend connection"
                  >
                    <span className="live-dot pulse"></span>
                    <span>Backend: {backendUrl.replace('https://', '').replace('http://', '').replace('/ascona-16526/us-central1/api', '')}</span>
                    <span style={{ fontSize: '0.7rem', opacity: 0.75 }}>{showBackendConfig ? '▲' : '⚙'}</span>
                  </button>
                </div>
                <div className="output-header-right">
                  {syncStatus === 'synced' ? (
                    <span style={{ color: '#34d399', fontSize: '0.82rem', fontWeight: 600 }}>
                      ✓ Synced to Jira Cloud
                    </span>
                  ) : syncStatus === 'syncing' ? (
                    <span style={{ color: '#38bdf8', fontSize: '0.82rem', fontWeight: 600 }}>
                      ⏳ Transmitting REST Payload...
                    </span>
                  ) : (
                    <button
                      onClick={handleSimulateSync}
                      className="btn-secondary-dark btn-sm"
                      title="Simulate Atlassian REST API sync"
                    >
                      Sync to Jira Cloud ↗
                    </button>
                  )}
                </div>
              </div>

              {/* Expandable Live Backend Configuration Panel */}
              {showBackendConfig && (
                <div className="backend-config-drawer">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>⚡ Ascona Backend Connection (Desktop/ascona-backend)</span>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary-dark btn-sm"
                      onClick={() => setShowBackendConfig(false)}
                      style={{ padding: '2px 8px', fontSize: '0.72rem' }}
                    >
                      ✕ Close
                    </button>
                  </div>
                  <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0 0 10px 0', lineHeight: 1.4 }}>
                    Live simulator calls <code>/inference/backlog</code> and <code>/inference/sprint-plan</code>. Supports the production cloud backend, local Firebase Functions emulator, or Python FastAPI service.
                  </p>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.74rem', color: '#64748b', alignSelf: 'center' }}>Presets:</span>
                    <button
                      type="button"
                      className="backend-preset-btn"
                      onClick={() => setBackendUrl('https://app.ascona.ai/api')}
                    >
                      Cloud: app.ascona.ai/api
                    </button>
                    <button
                      type="button"
                      className="backend-preset-btn"
                      onClick={() => setBackendUrl('http://localhost:5001/ascona-16526/us-central1/api/api')}
                    >
                      Local Emulator :5001
                    </button>
                    <button
                      type="button"
                      className="backend-preset-btn"
                      onClick={() => setBackendUrl('http://localhost:8000')}
                    >
                      Local Python :8000
                    </button>
                  </div>
                  <div className="backend-config-row">
                    <input
                      type="text"
                      className="backend-config-input"
                      value={backendUrl}
                      onChange={(e) => setBackendUrl(e.target.value)}
                      placeholder="Backend Base URL (e.g. https://app.ascona.ai/api)"
                    />
                    <input
                      type="text"
                      className="backend-config-input"
                      value={backendAuthToken}
                      onChange={(e) => setBackendAuthToken(e.target.value)}
                      placeholder="Optional Bearer Auth Token (asc_live_...)"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-secondary-dark btn-sm"
                      onClick={handleTestBackendConnection}
                      disabled={isPinging}
                    >
                      {isPinging ? 'Pinging...' : '⚡ Test Connection'}
                    </button>
                    <button
                      type="button"
                      className="btn-primary-gradient btn-sm"
                      onClick={handleSaveBackendConfig}
                      style={{ padding: '5px 14px' }}
                    >
                      ✓ Save & Use
                    </button>
                    {pingStatus && (
                      <span style={{ fontSize: '0.78rem', color: pingStatus.startsWith('✓') ? '#34d399' : '#f59e0b', fontWeight: 600 }}>
                        {pingStatus}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Generation Success Banner */}
              {generationMetadata && (
                <div className="generation-success-banner">
                  <div className="banner-left">
                    <span className="banner-pulse"></span>
                    <span className="banner-title">✨ Generated Backlog & Sprint Matrix: {generationMetadata.title}</span>
                  </div>
                  <div className="banner-stats">
                    <span>{generationMetadata.storiesCount} Stories</span>
                    <span>·</span>
                    <span>{generationMetadata.totalPoints} Story Pts</span>
                    <span>·</span>
                    <span>{generationMetadata.sprintsCount} Sprints</span>
                    <span>·</span>
                    <span className="banner-time">{generationMetadata.timestamp}</span>
                  </div>
                </div>
              )}

              {/* Multi-View Tabs (reused from Desktop/ascona architecture) */}
              <div className="simulator-view-tabs">
                <button
                  type="button"
                  className={`simulator-view-btn ${activeSimulatorView === 'backlog' ? 'active' : ''}`}
                  onClick={() => setActiveSimulatorView('backlog')}
                >
                  📋 Jira Backlog <span className="tab-count-pill">{activeStories.length}</span>
                </button>
                <button
                  type="button"
                  className={`simulator-view-btn ${activeSimulatorView === 'sprint-plan' ? 'active' : ''}`}
                  onClick={() => setActiveSimulatorView('sprint-plan')}
                >
                  📊 Sprint Plan Matrix <span className="tab-count-pill">{activeSprintPlan.length} Sprints</span>
                </button>
                <button
                  type="button"
                  className={`simulator-view-btn ${activeSimulatorView === 'prfaq' ? 'active' : ''}`}
                  onClick={() => setActiveSimulatorView('prfaq')}
                >
                  📄 Amazon PRFAQ <span className="tab-count-pill">Spec</span>
                </button>
                <button
                  type="button"
                  className={`simulator-view-btn ${activeSimulatorView === 'cards' ? 'active' : ''}`}
                  onClick={() => setActiveSimulatorView('cards')}
                >
                  🗂️ Board Cards <span className="tab-count-pill">{activeCards.length}</span>
                </button>
              </div>

              {/* In-Progress Generation Overlay */}
              {isGenerating && (
                <div className="generating-overlay">
                  <div className="generating-spinner"></div>
                  <div className="generating-step-text">{generationStep}</div>
                  <div className="generating-progress-bar">
                    <div className="generating-progress-fill"></div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    Decomposing architectural specs, synthesizing Amazon PRFAQ, 16-field Agile Sprint Matrix & Gherkin scenarios...
                  </div>
                </div>
              )}

              {/* VIEW 1: JIRA BACKLOG & GHERKIN CRITERIA */}
              {!isGenerating && activeSimulatorView === 'backlog' && (
                <>
                  <div className="output-epic-card">
                    <span className="epic-tag">Epic · {activeEpic.key}</span>
                    <h4 className="epic-title">{activeEpic.title}</h4>
                    <p className="epic-desc">{activeEpic.description}</p>
                  </div>

                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.05em' }}>
                    Generated Stories ({activeStories.length}) — Click to inspect Gherkin Acceptance Criteria:
                  </div>

                  <div className="stories-list">
                    {activeStories.map((story, sIdx) => (
                      <div
                        key={story.key}
                        onClick={() => setSelectedStoryIndex(sIdx)}
                        className={`story-card ${selectedStoryIndex === sIdx ? 'selected' : ''}`}
                      >
                        <div className="story-card-top">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className="issue-key">{story.key}</span>
                            {story.sprint && (
                              <span className="sprint-tag-badge">Sprint {story.sprint}</span>
                            )}
                          </div>
                          <span className="story-pts-badge">{(story.storyPoints || story.points)} Story Pts</span>
                        </div>
                        <div className="story-title">{story.title}</div>

                        {story.description && (
                          <div className="persona-story-desc">
                            {story.description}
                          </div>
                        )}

                        <div className="story-meta-row">
                          {story.labels && story.labels.map(l => (
                            <span key={l} className="story-label-pill">{l}</span>
                          ))}
                          {story.prerequisites && story.prerequisites.length > 0 && (
                            <span className="story-prereq-pill">Prereq: {story.prerequisites.join(', ')}</span>
                          )}
                        </div>

                        {selectedStoryIndex === sIdx && (
                          <div className="gherkin-snippet">
                            <div className="gherkin-snippet-header">
                              <span>Given-When-Then Acceptance Criteria (Gherkin BDD)</span>
                              <button
                                type="button"
                                className="copy-gherkin-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyGherkin(story.gherkin, story.key);
                                }}
                              >
                                {copiedKey === story.key ? '✓ Copied!' : 'Copy Gherkin'}
                              </button>
                            </div>
                            {story.gherkin}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* VIEW 2: SPRINT PLAN TABLE (reused from Desktop/ascona SprintPlanTable) */}
              {!isGenerating && activeSimulatorView === 'sprint-plan' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                      16-Field Agile Sprint Plan Matrix
                    </span>
                    <button
                      type="button"
                      className="btn-secondary-dark btn-sm"
                      onClick={() => handleExportSprintPlanCsv(activeSprintPlan)}
                    >
                      Export CSV ↓
                    </button>
                  </div>
                  <div className="sprint-matrix-hint">⇄ Swipe horizontally to inspect all 16 sprint columns</div>

                  <div className="sprint-matrix-wrapper">
                    <table className="sprint-matrix-table">
                      <thead>
                        <tr>
                          <th>Story</th>
                          <th>Expected Tasks</th>
                          <th>Resource</th>
                          <th>Sprint ID</th>
                          <th>Plan Start</th>
                          <th>Plan End</th>
                          <th>Ticket ID</th>
                          <th>Estimate</th>
                          <th>Deliverable</th>
                          <th>Status</th>
                          <th>Artifacts</th>
                          <th>Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeSprintPlan.map((row, rIdx) => (
                          <tr key={rIdx}>
                            <td><strong>{row["Story"]}</strong></td>
                            <td>{row["Expected Tasks"]}</td>
                            <td><span style={{ color: '#38bdf8' }}>{row["Resource Name"]}</span></td>
                            <td><code>{row["Sprint ID"]}</code></td>
                            <td>{row["Plan Start Date"]}</td>
                            <td>{row["Plan End Date"]}</td>
                            <td><code>{row["Ticket ID"]}</code></td>
                            <td><span style={{ color: '#34d399', fontWeight: 700 }}>{row["Estimate (in Days)"]}d</span></td>
                            <td>{row["Deliverable"]}</td>
                            <td>
                              <span className={`matrix-status-pill ${
                                row["Status"] === 'In Progress' ? 'status-in-progress' :
                                row["Status"] === 'Ready for Review' ? 'status-ready' : 'status-review'
                              }`}>
                                {row["Status"]}
                              </span>
                            </td>
                            <td>{row["Artifacts"]}</td>
                            <td style={{ color: '#94a3b8' }}>{row["Comments"]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* VIEW 3: AMAZON PRFAQ DOCUMENT (reused from Desktop/ascona prfaq) */}
              {!isGenerating && activeSimulatorView === 'prfaq' && (
                <div>
                  <div className="prfaq-header">
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                      Generated Amazon-Style PRFAQ
                    </span>
                    <button
                      type="button"
                      className="btn-secondary-dark btn-sm"
                      onClick={() => handleCopyPrfaq(activePrfaq)}
                    >
                      {copiedPrfaq ? '✓ Copied PRFAQ!' : 'Copy PRFAQ Text'}
                    </button>
                  </div>
                  <div className="prfaq-box">
                    {activePrfaq}
                  </div>
                </div>
              )}

              {/* VIEW 4: KANBAN BOARD CARDS (reused from Desktop/ascona trello_cards) */}
              {!isGenerating && activeSimulatorView === 'cards' && (
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '10px' }}>
                    Actionable Board Cards ({activeCards.length})
                  </div>
                  <div className="kanban-cards-grid">
                    {activeCards.map((card, cIdx) => (
                      <div key={cIdx} className="kanban-card">
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 700 }}>Card #{cIdx + 1}</span>
                            <span className="story-pts-badge">{card.points || 5} pts</span>
                          </div>
                          <h5 className="kanban-card-title">{card.name}</h5>
                          <p className="kanban-card-desc">{card.text || card.description}</p>
                        </div>
                        <button
                          type="button"
                          className="kanban-card-btn"
                          onClick={() => handleCreateCard(card, cIdx)}
                        >
                          {createdCardIndex === cIdx ? '✓ Added to Board' : '+ Create in Board'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Product Tour & Architecture Section (Tangible Proof) */}
      <section id="product-tour" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Interactive Product Architecture</span>
          <h2 className="section-title">See Ascona Orchestrate Across Your Stack</h2>
          <p className="section-desc">
            No vaporware. Explore how Ascona ingests raw product requirements, synthesizes executive PRFAQs, balances 16-field Agile sprint plans, and syncs directly into Atlassian Jira and Linear without database migration.
          </p>
        </div>

        <div className="product-tour-tabs">
          <button
            type="button"
            className={`tour-tab-btn ${activeTourTab === 'jira-sync' ? 'active' : ''}`}
            onClick={() => setActiveTourTab('jira-sync')}
          >
            <span>🔷 Bi-Directional Jira & Linear Sync</span>
          </button>
          <button
            type="button"
            className={`tour-tab-btn ${activeTourTab === 'prd-prfaq' ? 'active' : ''}`}
            onClick={() => setActiveTourTab('prd-prfaq')}
          >
            <span>📄 PRD to Amazon PRFAQ</span>
          </button>
          <button
            type="button"
            className={`tour-tab-btn ${activeTourTab === 'sprint-matrix' ? 'active' : ''}`}
            onClick={() => setActiveTourTab('sprint-matrix')}
          >
            <span>📊 16-Field Agile Sprint Matrix</span>
          </button>
          <button
            type="button"
            className={`tour-tab-btn ${activeTourTab === 'mcp-cli' ? 'active' : ''}`}
            onClick={() => setActiveTourTab('mcp-cli')}
          >
            <span>💻 IDE & Terminal MCP Protocol</span>
          </button>
        </div>

        <div className="product-tour-card">
          <div className="tour-card-header">
            <div className="window-dots">
              <span className="window-dot dot-red"></span>
              <span className="window-dot dot-yellow"></span>
              <span className="window-dot dot-green"></span>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: '#94a3b8' }}>
              ascona-orchestration-engine · {activeTourTab}.view
            </div>
            <div style={{ fontSize: '0.78rem', color: '#34d399', fontWeight: 600 }}>
              ● Live Synchronized View
            </div>
          </div>

          <div className="tour-body-grid">
            {activeTourTab === 'jira-sync' && (
              <>
                <div className="tour-sidebar">
                  <span className="tour-badge-pill">Zero Tool Migration</span>
                  <h3 className="tour-title">Two-Way Jira & Linear Enrichment Layer</h3>
                  <p className="tour-desc">
                    Ascona doesn't ask you to migrate databases. It connects directly to your Atlassian Jira Cloud, Jira Data Center, or Linear workspaces via REST API webhooks, enriching existing boards with parent Epics, calibrated Fibonacci points, and Given-When-Then criteria.
                  </p>
                  <ul className="tour-features-list">
                    <li><span className="bullet-check">✓</span> <span>Custom Jira issue schema mapping (Epics, Stories, Bugs, Tasks)</span></li>
                    <li><span className="bullet-check">✓</span> <span>Bi-directional field sync with live issue keys (e.g. ASCONA-ENG)</span></li>
                    <li><span className="bullet-check">✓</span> <span>Zero duplicate tickets or broken board workflows</span></li>
                  </ul>
                  <div style={{ marginTop: '10px' }}>
                    <a href="#signup" onClick={scrollTo('signup')} className="btn-primary-gradient btn-sm">
                      Connect Your Jira Workspace →
                    </a>
                  </div>
                </div>

                <div className="tour-canvas-pane">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '8px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: 700, textTransform: 'uppercase' }}>Atlassian Jira Cloud · Live Sync Engine</span>
                    <span style={{ fontSize: '0.72rem', background: 'rgba(52,211,153,0.15)', color: '#34d399', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>✓ Synced: 200 OK</span>
                  </div>

                  <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '6px', padding: '12px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '0.7rem', color: '#818cf8', fontWeight: 700 }}>EPIC · ASCONA-401</span>
                      <span style={{ fontSize: '0.7rem', color: '#38bdf8', fontWeight: 700 }}>Sprint 12 · 24 Pts Total</span>
                    </div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>Real-Time WebRTC Streaming & PII Guardrails Engine</div>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Decomposed into 4 stories with automated Given-When-Then test criteria.</div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ background: '#090d16', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '6px', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.72rem', color: '#818cf8', fontFamily: 'var(--font-mono)' }}>ASCONA-402</span>
                        <div style={{ fontSize: '0.82rem', color: '#f1f5f9', fontWeight: 600 }}>WebRTC PeerConnection negotiation with REST fallback</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <span style={{ fontSize: '0.7rem', background: 'rgba(56,189,248,0.12)', color: '#38bdf8', padding: '1px 6px', borderRadius: '4px' }}>5 pts</span>
                        <span style={{ fontSize: '0.7rem', background: 'rgba(52,211,153,0.15)', color: '#34d399', padding: '1px 6px', borderRadius: '4px' }}>In Progress</span>
                      </div>
                    </div>
                    <div style={{ background: '#090d16', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '6px', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.72rem', color: '#818cf8', fontFamily: 'var(--font-mono)' }}>ASCONA-403</span>
                        <div style={{ fontSize: '0.82rem', color: '#f1f5f9', fontWeight: 600 }}>Zero-latency regex & NER PII redaction pipeline</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <span style={{ fontSize: '0.7rem', background: 'rgba(56,189,248,0.12)', color: '#38bdf8', padding: '1px 6px', borderRadius: '4px' }}>8 pts</span>
                        <span style={{ fontSize: '0.7rem', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', padding: '1px 6px', borderRadius: '4px' }}>Ready for Dev</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTourTab === 'prd-prfaq' && (
              <>
                <div className="tour-sidebar">
                  <span className="tour-badge-pill">Working Backwards</span>
                  <h3 className="tour-title">Messy PRD to Executive Amazon PRFAQ</h3>
                  <p className="tour-desc">
                    Paste raw Notion notes, customer interview transcripts, or high-level RFC briefs. Ascona uses Amazon's proven Working Backwards methodology to generate an Internal Press Release, Customer FAQ, and Technical Risk FAQ.
                  </p>
                  <ul className="tour-features-list">
                    <li><span className="bullet-check">✓</span> <span>Internal Press Release articulating user outcomes before coding</span></li>
                    <li><span className="bullet-check">✓</span> <span>Customer FAQ resolving value proposition ambiguity</span></li>
                    <li><span className="bullet-check">✓</span> <span>Technical Architecture FAQ identifying rate-limits and database spikes</span></li>
                  </ul>
                  <div style={{ marginTop: '10px' }}>
                    <a href="#simulator" onClick={scrollTo('simulator')} className="btn-secondary-dark btn-sm">
                      Generate a Sample PRFAQ Live ↓
                    </a>
                  </div>
                </div>

                <div className="tour-canvas-pane">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#fbbf24', fontWeight: 700 }}>AMAZON WORKING BACKWARDS · RFC SPEC</span>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Doc ID: ASCONA-PRFAQ-04</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#cbd5e1', lineHeight: '1.6', fontFamily: 'var(--font-mono)', background: '#090d16', padding: '14px', borderRadius: '6px', maxHeight: '230px', overflowY: 'auto' }}>
                    <div style={{ color: '#38bdf8', fontWeight: 700, marginBottom: '6px' }}>PRESS RELEASE · SEATTLE, WA</div>
                    "Today, Engineering Leadership announced the release of the Zero-Latency WebRTC Voice Assistant. By integrating streaming token audio directly into client applications, enterprise teams reduce customer support handle time by 62% while enforcing strict PII redaction."
                    <br /><br />
                    <div style={{ color: '#34d399', fontWeight: 700, marginBottom: '4px' }}>CUSTOMER FAQ:</div>
                    Q: How does this maintain low latency under unstable networks?<br />
                    A: Client SDKs maintain dual-channel WebRTC connections, degrading gracefully to chunked REST if packet loss exceeds 8%.
                  </div>
                </div>
              </>
            )}

            {activeTourTab === 'sprint-matrix' && (
              <>
                <div className="tour-sidebar">
                  <span className="tour-badge-pill">Velocity Modeling</span>
                  <h3 className="tour-title">16-Field Agile Sprint Plan Matrix</h3>
                  <p className="tour-desc">
                    Stop debating story points for 2 hours in planning poker. Ascona calculates objective Fibonacci points grounded in database migrations, third-party API dependencies, and historical PR velocity.
                  </p>
                  <ul className="tour-features-list">
                    <li><span className="bullet-check">✓</span> <span>16-field Agile matrix with resource allocation and deliverables</span></li>
                    <li><span className="bullet-check">✓</span> <span>Critical path dependency modeling preventing blocked sprints</span></li>
                    <li><span className="bullet-check">✓</span> <span>Direct CSV and Atlassian Jira export</span></li>
                  </ul>
                  <div style={{ marginTop: '10px' }}>
                    <a href="#signup" onClick={scrollTo('signup')} className="btn-primary-gradient btn-sm">
                      Pilot Automated Sprint Sizing →
                    </a>
                  </div>
                </div>

                <div className="tour-canvas-pane">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#c084fc', fontWeight: 700 }}>16-FIELD SPRINT MATRIX · RESOURCE ALLOCATION</span>
                    <span style={{ fontSize: '0.72rem', color: '#34d399', fontWeight: 700 }}>Balanced Sprints</span>
                  </div>
                  <div style={{ fontSize: '0.74rem', background: '#090d16', padding: '10px', borderRadius: '6px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '400px' }}>
                      <thead>
                        <tr style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <th style={{ padding: '6px' }}>Story</th>
                          <th style={{ padding: '6px' }}>Resource</th>
                          <th style={{ padding: '6px' }}>Estimate</th>
                          <th style={{ padding: '6px' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#f1f5f9' }}>
                          <td style={{ padding: '6px' }}>WebRTC Socket Gateway</td>
                          <td style={{ padding: '6px', color: '#38bdf8' }}>Backend Lead</td>
                          <td style={{ padding: '6px' }}>5 pts (3d)</td>
                          <td style={{ padding: '6px', color: '#34d399' }}>✓ Assigned</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#f1f5f9' }}>
                          <td style={{ padding: '6px' }}>PII NER Sanitizer</td>
                          <td style={{ padding: '6px', color: '#c084fc' }}>ML Engineer</td>
                          <td style={{ padding: '6px' }}>8 pts (4d)</td>
                          <td style={{ padding: '6px', color: '#fbbf24' }}>⏳ Critical Path</td>
                        </tr>
                        <tr style={{ color: '#f1f5f9' }}>
                          <td style={{ padding: '6px' }}>Audio Visualizer UI</td>
                          <td style={{ padding: '6px', color: '#38bdf8' }}>Frontend Lead</td>
                          <td style={{ padding: '6px' }}>3 pts (1.5d)</td>
                          <td style={{ padding: '6px', color: '#34d399' }}>✓ Assigned</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {activeTourTab === 'mcp-cli' && (
              <>
                <div className="tour-sidebar">
                  <span className="tour-badge-pill">Developer Ergonomics</span>
                  <h3 className="tour-title">Model Context Protocol (MCP) & Terminal CLI</h3>
                  <p className="tour-desc">
                    Engineers shouldn't have to live in heavy browser UIs. Run Ascona directly from your terminal or plug it natively into Cursor, Claude Desktop, and Antigravity IDE via Model Context Protocol.
                  </p>
                  <ul className="tour-features-list">
                    <li><span className="bullet-check">✓</span> <span>Local JSON-RPC MCP server for Cursor and AI agent orchestration</span></li>
                    <li><span className="bullet-check">✓</span> <span>Headless CLI: <code>ascona parse ./spec.md --sync-jira</code></span></li>
                    <li><span className="bullet-check">✓</span> <span>GitHub Actions CI/CD bot for automated PRD ticket enrichment</span></li>
                  </ul>
                  <div style={{ marginTop: '10px' }}>
                    <a href="/userGuide.html#cli" target="_blank" rel="noopener noreferrer" className="btn-secondary-dark btn-sm">
                      Read Developer CLI Docs ↗
                    </a>
                  </div>
                </div>

                <div className="tour-canvas-pane">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: 700 }}>TERMINAL SHELL · @ASCONA/CLI</span>
                    <span style={{ fontSize: '0.72rem', color: '#34d399' }}>MCP Server Ready</span>
                  </div>
                  <div style={{ fontSize: '0.76rem', color: '#f1f5f9', fontFamily: 'var(--font-mono)', background: '#090d16', padding: '12px', borderRadius: '6px', lineHeight: '1.55' }}>
                    <span style={{ color: '#38bdf8' }}>$ ascona generate</span> --input ./docs/auth-rbac.md --sync-jira<br />
                    <span style={{ color: '#94a3b8' }}>[1/4]</span> Parsing raw RFC specifications... <span style={{ color: '#34d399' }}>done [0.6s]</span><br />
                    <span style={{ color: '#94a3b8' }}>[2/4]</span> Structuring Epics & Gherkin scenarios... <span style={{ color: '#34d399' }}>done [1.2s]</span><br />
                    <span style={{ color: '#94a3b8' }}>[3/4]</span> Calibrating Fibonacci velocity points... <span style={{ color: '#34d399' }}>done [0.3s]</span><br />
                    <span style={{ color: '#34d399' }}>✔ 1 Epic + 5 Stories transmitted to Jira Cloud (SEC-101..106)</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Developer Tooling: Terminal CLI & Model Context Protocol (MCP) */}
      <section id="cli-mcp" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Developer Tooling & Protocols</span>
          <h2 className="section-title">Terminal CLI & Model Context Protocol (MCP)</h2>
          <p className="section-desc">
            Built for developers who live in their terminal and IDE. Run Ascona directly from your CLI or connect it natively to Cursor, Claude Desktop, Antigravity, and AI agents via MCP.
          </p>
        </div>

        <div className="cli-mcp-grid">
          {/* CLI Card */}
          <div className="dev-card">
            <div>
              <span className="dev-badge dev-badge-cli">Terminal CLI · @ascona/cli</span>
              <h3>Headless Backlog Refinement from the Shell</h3>
              <p>
                Parse product specs, calculate velocity-calibrated story points, and commit issues to Jira directly from your command line or GitHub Actions PR workflow.
              </p>
              <div className="terminal-box">
                <span style={{ color: '#94a3b8' }}># Install globally or run with npx</span><br />
                <span style={{ color: '#38bdf8' }}>$ npm i -g @ascona/cli</span><br /><br />
                <span style={{ color: '#94a3b8' }}># Decompose PRD & sync directly to Jira or GitHub</span><br />
                <span style={{ color: '#34d399' }}>$ ascona parse ./specs/billing-v2.md --sync-jira --project ENG</span><br />
                <span style={{ color: '#cbd5e1' }}>✔ Extracted 1 Epic & 4 Stories [1.4s]</span><br />
                <span style={{ color: '#cbd5e1' }}>✔ Calibrated Fibonacci Points (18 pts) [0.3s]</span><br />
                <span style={{ color: '#a5b4fc' }}>✔ Synced to Jira Cloud (ENG-401 → ENG-405)</span><br />
                <span style={{ color: '#34d399' }}>$ ascona export-github --repo acme/core-api --create-issues</span><br />
                <span style={{ color: '#38bdf8' }}>✔ Populated 4 GitHub Issues with sub-issues & Gherkin AC [0.8s]</span>
              </div>
            </div>
            <div>
              <a href="/userGuide.html#cli" target="_blank" rel="noopener noreferrer" className="btn-secondary-dark btn-sm">
                View Full CLI Documentation ↗
              </a>
            </div>
          </div>

          {/* MCP Card */}
          <div className="dev-card">
            <div>
              <span className="dev-badge dev-badge-mcp">Model Context Protocol · @ascona/mcp-server</span>
              <h3>Native Agentic Integration in Cursor & Claude</h3>
              <p>
                Equip your AI coding agent with direct Jira & GitHub backlog intelligence. Ask Cursor or Claude to inspect acceptance criteria, generate subtasks, or update story points without switching context.
              </p>
              <div className="terminal-box">
                <span style={{ color: '#94a3b8' }}>{"// claude_desktop_config.json or .cursor/mcp.json"}</span><br />
                <span style={{ color: '#c4b5fd' }}>"mcpServers"</span>: &#123;<br />
                &nbsp;&nbsp;<span style={{ color: '#38bdf8' }}>"ascona"</span>: &#123;<br />
                &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#cbd5e1' }}>"command"</span>: <span style={{ color: '#34d399' }}>"npx"</span>,<br />
                &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#cbd5e1' }}>"args"</span>: [<span style={{ color: '#34d399' }}>"-y"</span>, <span style={{ color: '#34d399' }}>"@ascona/mcp-server"</span>],<br />
                &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#cbd5e1' }}>"env"</span>: &#123; <span style={{ color: '#38bdf8' }}>"ASCONA_API_KEY"</span>: <span style={{ color: '#34d399' }}>"asc_live_..."</span> &#125;<br />
                &nbsp;&nbsp;&#125;<br />
                &#125;
              </div>

              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Exposed Agent MCP Tools:
              </div>
              <div className="tools-pills-row">
                <span className="tool-pill">ascona_decompose_prd</span>
                <span className="tool-pill">ascona_sync_jira</span>
                <span className="tool-pill">ascona_sync_github</span>
                <span className="tool-pill">ascona_generate_gherkin</span>
                <span className="tool-pill">ascona_calibrate_points</span>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <a href="/userGuide.html#mcp" target="_blank" rel="noopener noreferrer" className="btn-secondary-dark btn-sm">
                View MCP Schemas & Signatures ↗
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture & Workflow Showcase */}
      <section id="how-it-works" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Architecture & Engineering Pipeline</span>
          <h2 className="section-title">Built for Modern Agile Workflows</h2>
          <p className="section-desc">
            Ascona is not another generic chatbot wrapper. It is an autonomous, domain-aware pipeline engineered specifically to interface with enterprise issue tracking systems.
          </p>
        </div>

        <div className="mockup-container">
          <div className="mockup-tabs-bar">
            {SHOWCASE_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveShowcaseTab(tab.id)}
                className={`mockup-tab-btn ${activeShowcaseTab === tab.id ? 'active' : ''}`}
              >
                {tab.title}
              </button>
            ))}
          </div>

          <div className="mockup-content">
            <div className="mockup-text">
              <h4>{activeShowcase.subtitle}</h4>
              <p>{activeShowcase.description}</p>
              <ul className="mockup-bullets">
                {activeShowcase.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
              <div>
                <a href="#signup" onClick={scrollTo('signup')} className="btn-primary-gradient btn-sm">
                  Schedule Technical Walkthrough →
                </a>
              </div>
            </div>

            <div className="mockup-visual">
              <pre style={{ margin: 0, color: '#38bdf8', overflowX: 'auto', lineHeight: 1.55 }}>
                {activeShowcase.code}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Pillars Grid */}
      <section id="features" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Core Capabilities</span>
          <h2 className="section-title">Everything Engineering Squads Need to Ship on Time</h2>
          <p className="section-desc">
            Bridges the costly communication gap between Product Managers, Engineering Leads, and QA Automators.
          </p>
        </div>

        <div className="features-grid-modern">
          <div className="feature-card-modern">
            <div className="feature-icon-wrapper">📐</div>
            <h3>Autonomous Spec Parsing</h3>
            <p>
              Ingest PRDs, Notion docs, Figma specs, or Slack threads. Ascona extracts technical requirements, user stories, and edge cases with zero manual formatting required.
            </p>
          </div>

          <div className="feature-card-modern">
            <div className="feature-icon-wrapper">🧪</div>
            <h3>Enterprise Gherkin Criteria</h3>
            <p>
              Every ticket includes testable Given-When-Then scenarios. Your QA engineers can plug them straight into Playwright, Cypress, or Cucumber pipelines without rewriting.
            </p>
          </div>

          <div className="feature-card-modern">
            <div className="feature-icon-wrapper">⚖️</div>
            <h3>Calibrated Fibonacci Points</h3>
            <p>
              Say goodbye to 2-hour planning poker debates. Ascona evaluates schema complexity, 3rd party APIs, and risk factors to calculate objective story points.
            </p>
          </div>

          <div className="feature-card-modern">
            <div className="feature-icon-wrapper">🔄</div>
            <h3>2-Way Atlassian Sync</h3>
            <p>
              Native Atlassian Cloud & Data Center sync. Automatically sets parent Epics, child Subtasks, sprint milestones, and custom metadata fields in seconds.
            </p>
          </div>

          <div className="feature-card-modern">
            <div className="feature-icon-wrapper">🛡️</div>
            <h3>SOC2 & Zero-Data Retention</h3>
            <p>
              Enterprise-grade privacy. Your product briefs and code repositories are never retained or used to train public LLM models. Built for regulated SaaS & Fintech.
            </p>
          </div>

          <div className="feature-card-modern">
            <div className="feature-icon-wrapper">🚨</div>
            <h3>Architectural Risk Detection</h3>
            <p>
              Ascona flags missing database rollback scripts, unhandled API rate limits, circular dependencies, and security compliance blind spots before your sprint starts.
            </p>
          </div>
        </div>
      </section>

      {/* The Upstream Paradigm Shift: Ascona vs Traditional PM Software */}
      <section id="github-comparison" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">The Upstream Paradigm Shift</span>
          <h2 className="section-title">Traditional PM Tools Track Work. Ascona Synthesizes It.</h2>
          <p className="section-desc">
            Conventional project management software—from Jira and Linear to GitHub Projects and Azure DevOps—provides boards, tables, and issue trackers. But they expect humans to manually type tickets, decompose sub-issues, and guess estimates. Ascona is the autonomous upstream engine that turns raw specifications into production backlogs in 30 seconds.
          </p>
        </div>

        <div className="github-diff-grid">
          {/* Card 1: Requirements Decomposition */}
          <div className="github-diff-card">
            <div>
              <div className="diff-card-header">
                <div className="diff-card-icon">📐</div>
                <div>
                  <h4 className="diff-card-title">Backlog Decomposition</h4>
                  <div className="diff-card-subtitle">From Raw Spec to Issue Hierarchy</div>
                </div>
              </div>

              <div className="diff-compare-stack">
                <div className="diff-item github-way">
                  <strong>✕ Traditional PM Tools & Trackers</strong>
                  Blank boards and empty tables. Requires PMs and tech leads to spend 15+ hours weekly manually typing issue cards, clicking "+ Add Subtask", and formatting tickets from scratch.
                </div>
                <div className="diff-item ascona-way">
                  <strong>✓ Ascona AI Autonomous Engine</strong>
                  Ingests multi-page Notion PRDs, RFCs, or feature briefs. Autonomously identifies domain boundaries, structuring technical Epics, Persona Stories, and Subtasks in 30 seconds.
                </div>
              </div>
            </div>

            <div className="diff-impact-badge">
              ⚡ Reclaim 15+ Hours / Week
            </div>
          </div>

          {/* Card 2: Acceptance Criteria & QA Readiness */}
          <div className="github-diff-card">
            <div>
              <div className="diff-card-header">
                <div className="diff-card-icon">🧪</div>
                <div>
                  <h4 className="diff-card-title">Acceptance Criteria & QA</h4>
                  <div className="diff-card-subtitle">Testable Verification Standards</div>
                </div>
              </div>

              <div className="diff-compare-stack">
                <div className="diff-item github-way">
                  <strong>✕ Conventional Task Systems</strong>
                  Plain markdown text boxes with vague bullet points. QA engineers and SDETs must reverse-engineer test cases, leading to missed edge cases and sprint roll-overs.
                </div>
                <div className="diff-item ascona-way">
                  <strong>✓ Ascona AI Autonomous Engine</strong>
                  Generates standardized Given-When-Then Gherkin scenarios for happy paths, timeouts, and security edge cases—directly copyable into Playwright, Cypress, and Cucumber test suites.
                </div>
              </div>
            </div>

            <div className="diff-impact-badge">
              🎯 100% Gherkin BDD Test Coverage
            </div>
          </div>

          {/* Card 3: Estimation & Resource Allocation */}
          <div className="github-diff-card">
            <div>
              <div className="diff-card-header">
                <div className="diff-card-icon">📊</div>
                <div>
                  <h4 className="diff-card-title">Velocity & Sprint Planning</h4>
                  <div className="diff-card-subtitle">Objective Sizing & Capacity</div>
                </div>
              </div>

              <div className="diff-compare-stack">
                <div className="diff-item github-way">
                  <strong>✕ Legacy Planning Boards</strong>
                  Basic calendar dates and manual estimate fields. Teams waste hours in planning poker debating story point numbers with zero objective architectural calibration.
                </div>
                <div className="diff-item ascona-way">
                  <strong>✓ Ascona AI Autonomous Engine</strong>
                  Algorithmic Fibonacci story points (1, 2, 3, 5, 8, 13) calibrated against schema migrations, rate limits, and external APIs—plus a complete 16-field Agile Sprint Plan matrix.
                </div>
              </div>
            </div>

            <div className="diff-impact-badge">
              📈 Zero Planning Poker Debates
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          Institutional Diligence & Defensibility Hub (Unified Tabbed Architecture)
          ========================================================================== */}
      <section id="diligence" className="section-wrapper diligence-hub-section">
        {/* Invisible anchor targets for backward-compatible deep links */}
        <div id="team-pedigree" style={{ position: 'relative', top: '-120px' }}></div>
        <div id="platform-moat" style={{ position: 'relative', top: '-120px' }}></div>
        <div id="discovery-proof" style={{ position: 'relative', top: '-120px' }}></div>
        <div id="capital-allocation" style={{ position: 'relative', top: '-120px' }}></div>
        <div id="strategic-value" style={{ position: 'relative', top: '-120px' }}></div>

        <div className="section-header">
          <span className="section-tag">Architecture &amp; Defensibility Hub</span>
          <h2 className="section-title">The Spec-to-PR Control Plane: Architecture &amp; Defensibility</h2>
          <p className="section-desc">
            Explore multi-repo AST synthesis, deterministic CI contract gates, verified production telemetry across 38 active squads, and enterprise security governance.
          </p>
        </div>

        {/* Tab Navigation Controls */}
        <div className="diligence-tab-nav" role="tablist" aria-label="Architecture &amp; Defensibility Pillars">
          <button
            type="button"
            role="tab"
            aria-selected={activeDiligenceTab === 'moat'}
            className={`diligence-tab-btn ${activeDiligenceTab === 'moat' ? 'active' : ''}`}
            onClick={() => setActiveDiligenceTab('moat')}
          >
            <span className="tab-icon">🛡️</span> Defensible Moat &amp; Engine
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeDiligenceTab === 'proof'}
            className={`diligence-tab-btn ${activeDiligenceTab === 'proof' ? 'active' : ''}`}
            onClick={() => setActiveDiligenceTab('proof')}
          >
            <span className="tab-icon">📊</span> Customer Proof &amp; Telemetry
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeDiligenceTab === 'governance'}
            className={`diligence-tab-btn ${activeDiligenceTab === 'governance' ? 'active' : ''}`}
            onClick={() => setActiveDiligenceTab('governance')}
          >
            <span className="tab-icon">🔒</span> Enterprise Governance &amp; Security
          </button>
        </div>

        {/* Diligence Tab Content Panes */}
        <div className="diligence-tab-content">

          {/* TAB 2: Defensible Moat & Bayesian Engine */}
          {activeDiligenceTab === 'moat' && (
            <div className="diligence-tab-pane">
              <div className="diligence-pane-header">
                <span className="pane-kicker">Architectural Sovereignty &amp; Data Moat</span>
                <h3>The Spec-to-PR Control Plane vs. Single-Repo Code Agents &amp; Trackers</h3>
                <p>
                  Venture investors ask: <em>"What happens when Cursor builds PM agents, or Atlassian rolls out Rovo enterprise-wide?"</em> Ascona's defensibility lies in being the neutral <strong>Spec-to-PR Control Plane</strong>—governing engineering scope and deterministic CI contract gates across human developers and autonomous coding agents across 3–50+ multi-repo codebases.
                </p>
              </div>

              {/* Slide 05 3-Way Architectural Comparison */}
              <div className="moat-comparison-box">
                <div className="moat-comp-header">
                  <span className="moat-comp-badge">Slide 05 Diligence Matrix</span>
                  <h4>Why In-Editor Code Agents &amp; Issue Trackers Cannot Displace Ascona</h4>
                </div>
                <div className="moat-3col-grid">
                  <div className="moat-3col-card agent-gap">
                    <span className="moat-3col-tag tag-gap">In-Editor Code Agents</span>
                    <h5>Cursor, Claude Code &amp; Copilot</h5>
                    <p className="moat-3col-sub">Single-Repo Session Blindness</p>
                    <ul className="moat-3col-list">
                      <li>✕ Confined to single-repo local window (<code>.cursorrules</code>, <code>CLAUDE.md</code>)</li>
                      <li>✕ Blind to cross-repo dependencies and upstream microservice schemas</li>
                      <li>✕ Generates syntactically correct code that breaks production API contracts</li>
                      <li>✕ Zero awareness of Jira sprint velocity, epics, or compliance schemas</li>
                    </ul>
                  </div>

                  <div className="moat-3col-card tracker-gap">
                    <span className="moat-3col-tag tag-gap">Issue Trackers &amp; Search</span>
                    <h5>Jira + Rovo &amp; Linear</h5>
                    <p className="moat-3col-sub">Passive Status Containers</p>
                    <ul className="moat-3col-list">
                      <li>✕ Track tickets as passive databases after code is already written</li>
                      <li>✕ Rovo only searches Atlassian Cloud docs; cannot inspect local ASTs</li>
                      <li>✕ Zero deterministic CI contract gates to block breaking agent PRs</li>
                      <li>✕ Requires 10+ hours of manual grooming poker debates every sprint</li>
                    </ul>
                  </div>

                  <div className="moat-3col-card ascona-moat">
                    <span className="moat-3col-tag tag-winner">The Control Plane</span>
                    <h5>Ascona AI Engine</h5>
                    <p className="moat-3col-sub">Cross-Repo Spec-to-PR Control</p>
                    <ul className="moat-3col-list">
                      <li>✓ Multi-repo AST indexing across 3–50+ repos (Go, TS, Python, Rust)</li>
                      <li>✓ Decomposes raw RFCs into Given-When-Then Gherkin acceptance criteria</li>
                      <li>✓ Deterministic GitHub Actions CI gate (<code>ascona-action</code>, 98.2% precision)</li>
                      <li>✓ Native 2-way Jira Cloud REST v3 sync with zero tool migration required</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 4 Architectural Moat Pillars */}
              <div className="moat-architecture-grid" style={{ marginTop: '24px' }}>
                <div className="moat-card">
                  <div>
                    <span className="moat-tag ast">Multi-Repo Cross-Stack ASTs</span>
                    <h3 className="moat-title">Multi-Repo AST Graph vs. Single-Repo Session Blindness</h3>
                    <p className="moat-desc">
                      Modern SaaS runs on 3–50+ microservices. Coding agents in Cursor or Claude Code are trapped in single-repo context windows. Through <code>@ascona/mcp-server</code>, Ascona indexes cross-repo type definitions, protobufs, and API contracts simultaneously to catch breaking schema drift before code is committed.
                    </p>
                  </div>
                  <div className="moat-vs-box">
                    <strong>Coding Agent Blindspot:</strong> Cannot cross repository boundaries or detect schema collisions across microservices.
                  </div>
                </div>

                <div className="moat-card">
                  <div>
                    <span className="moat-tag jira">Deterministic CI Contract Gates</span>
                    <h3 className="moat-title">98.2% CI Precision vs. Unchecked Code Churn</h3>
                    <p className="moat-desc">
                      AI coding tools make code generation 10x faster, but 68% of sprints suffer from broken API contracts and scope drift. Ascona's GitHub Action (<code>ascona-action</code>) inspects PR AST diffs against approved Gherkin contract specifications at 98.2% precision—with 0 false-alarm gate disablements across 4,200+ PRs.
                    </p>
                  </div>
                  <div className="moat-vs-box">
                    <strong>Issue Tracker Gap:</strong> Jira and Linear cannot validate PR code diffs against spec contracts in CI pipelines.
                  </div>
                </div>

                <div className="moat-card">
                  <div>
                    <span className="moat-tag prfaq">Bidirectional System of Record</span>
                    <h3 className="moat-title">Native Jira REST v3 Graph vs. Tool Migration Lock-in</h3>
                    <p className="moat-desc">
                      Enterprises will never abandon Jira for a standalone startup tracker. Ascona sits upstream as the intelligent orchestration layer, bi-directionally mapping epics, user stories, and Gherkin BDD criteria directly into enterprise Jira Cloud (Forge v2.4 + REST v3) without requiring painful data migration.
                    </p>
                  </div>
                  <div className="moat-vs-box">
                    <strong>Zero Migration Friction:</strong> Sits directly on your existing Jira licenses co-termed via Atlassian Marketplace (App ID: 3398389661).
                  </div>
                </div>

                <div className="moat-card">
                  <div>
                    <span className="moat-tag privacy">Enterprise Sovereignty &amp; ZDR</span>
                    <h3 className="moat-title">Zero Data Retention (ZDR) vs. Multi-Tenant LLM Training</h3>
                    <p className="moat-desc">
                      Enterprise software buyers reject tools that use proprietary code or executive PRDs to train shared foundation models. Ascona operates an ephemeral memory inference pipeline with Zero Data Retention, AES-256 KMS encryption, TLS 1.3, and optional dedicated VPC deployment for defense &amp; fintech.
                    </p>
                  </div>
                  <div className="moat-vs-box">
                    <strong>Vendor Lock-in Shield:</strong> Neutral orchestration layer between your git hosting (GitHub/GitLab) and ticket tracker (Jira/Linear).
                  </div>
                </div>
              </div>

              {/* Proprietary Data Moat & Flywheel (Slide 09) */}
              <div className="telemetry-banner" style={{ margin: '28px 0 0 0' }}>
                <div className="telemetry-header-row">
                  <div className="telemetry-live-badge">
                    <span className="telemetry-live-dot"></span>
                    <span>Proprietary Data Moat &amp; Synthesis Flywheel (Slide 09)</span>
                  </div>
                  <span className="telemetry-source-tag">
                    Empirical Production Telemetry · 38 Active Engineering Squads
                  </span>
                </div>
                <div className="telemetry-grid">
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">14,800+</div>
                    <div className="telemetry-stat-lbl">Indexed Spec-to-PR Diff &amp; CI Pairs</div>
                  </div>
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">89%</div>
                    <div className="telemetry-stat-lbl">Pre-Sprint Breaking Drift Recall</div>
                  </div>
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">-44%</div>
                    <div className="telemetry-stat-lbl">Manual Spec Edits by Sprint 3</div>
                  </div>
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">91.4%</div>
                    <div className="telemetry-stat-lbl">3-Sprint Cross-Repo Module Reuse</div>
                  </div>
                </div>
              </div>

              {/* Bayesian Complexity Calibration Moat */}
              <div className="bayesian-moat-box" style={{ marginTop: '28px' }}>
                <div className="bayesian-header">
                  <div className="bayesian-title-group">
                    <span className="bayesian-badge">Frontier LLM Moat · Bayesian Calibration</span>
                    <h3>Why 2M-Token Frontier Models Cannot Displace Ascona</h3>
                    <p>
                      Frontier LLMs (Claude 3.5 Sonnet, GPT-4o) lack access to private git repositories, commit histories, and Jira velocity databases. Ascona calibrates story points and acceptance criteria using proprietary Bayesian complexity models grounded in actual codebase metrics rather than text hallucinations.
                    </p>
                  </div>
                </div>

                <div className="bayesian-formula-bar">
                  P(Story_Points | AST_Delta, Schema_Risk, Git_Velocity) ∝ P(AST_Delta | Points) × P(Schema_Risk | Points) × Prior(Velocity)
                </div>

                <div className="bayesian-factors-grid">
                  <div className="bayesian-factor-card">
                    <strong>1. Multi-Repo AST Delta</strong>
                    <p>Parses abstract syntax tree mutations, branching factors, and cross-repo function call depth.</p>
                  </div>
                  <div className="bayesian-factor-card">
                    <strong>2. Schema Migration Hazards</strong>
                    <p>Detects high-risk table locks, Prisma/TypeORM alter statements, and backwards-incompatible database contracts.</p>
                  </div>
                  <div className="bayesian-factor-card">
                    <strong>3. Git Velocity &amp; PR History</strong>
                    <p>Normalizes Fibonacci estimates against team-specific PR turnaround times and historical cycle completions.</p>
                  </div>
                  <div className="bayesian-factor-card">
                    <strong>4. Jira Schema Compliance</strong>
                    <p>Enforces mandatory enterprise Jira schema rules, component mappings, and audit transition gates.</p>
                  </div>
                </div>
              </div>

              {/* Product Gating & In-Product Conversion Trigger Matrix */}
              <div className="funnel-gate-box" style={{ marginTop: '28px' }}>
                <h4 className="funnel-gate-title">
                  <span>🔐</span> Explicit Product Gate: Free Developer Tools ($0) vs. Team Workspace ($6,000/yr Base) vs. Enterprise Control ($27,000/yr)
                </h4>
                <div className="gate-table-wrapper">
                  <table className="funnel-gate-table">
                    <thead>
                      <tr>
                        <th>Capability &amp; Workflow</th>
                        <th>Free CLI &amp; Cursor MCP ($0)</th>
                        <th>Team Workspace ($6,000/yr Base)</th>
                        <th>Enterprise Control ($27,000/yr)</th>
                        <th>In-Product Upgrade Trigger</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Spec &amp; RFC Decomposition</td>
                        <td><span className="gate-badge-free">Up to 25 specs/mo</span></td>
                        <td><span className="gate-badge-paid">Unlimited Specs</span></td>
                        <td><span className="gate-badge-paid">Unlimited Multi-Org</span></td>
                        <td>Team collaboration &amp; cloud sync</td>
                      </tr>
                      <tr>
                        <td>Codebase AST Indexing</td>
                        <td><span className="gate-badge-free">Single-Repo AST</span></td>
                        <td><span className="gate-badge-paid">Multi-Repo (Up to 10 Repos)</span></td>
                        <td><span className="gate-badge-paid">Unlimited Cross-Repo (3–50+)</span></td>
                        <td>Cross-microservice contract validation</td>
                      </tr>
                      <tr>
                        <td>Given-When-Then Gherkin Criteria</td>
                        <td><span className="gate-badge-free">Stdout / Markdown</span></td>
                        <td><span className="gate-badge-paid">Jira BDD &amp; Test Runners</span></td>
                        <td><span className="gate-badge-paid">CI Runner Auto-Execution</span></td>
                        <td>Pushing test cases directly to Jira issues</td>
                      </tr>
                      <tr>
                        <td>Bi-Directional Jira Cloud Sync (REST v3)</td>
                        <td><span className="check-no">✕ Gated</span></td>
                        <td><span className="gate-badge-paid">✓ Native 2-Way REST v3</span></td>
                        <td><span className="gate-badge-paid">✓ Custom Jira Schemas &amp; Data Center</span></td>
                        <td>Syncing Epics, Stories, &amp; Subtasks in 1-click</td>
                      </tr>
                      <tr>
                        <td>Deterministic GitHub Actions CI Gate</td>
                        <td><span className="check-no">✕ Gated</span></td>
                        <td><span className="gate-badge-paid">✓ <code>ascona-action</code> on PRs</span></td>
                        <td><span className="gate-badge-paid">✓ Custom Enterprise Gate Policies</span></td>
                        <td>Automated contract check on PR / commit</td>
                      </tr>
                      <tr>
                        <td>Shared Squad Estimation Memory</td>
                        <td><span className="check-no">✕ Gated</span></td>
                        <td><span className="gate-badge-paid">✓ Bayesian Historical Velocity</span></td>
                        <td><span className="gate-badge-paid">✓ Org-Wide Cross-Squad Calibration</span></td>
                        <td>Cross-sprint historical velocity calibration</td>
                      </tr>
                      <tr>
                        <td>Frictionless Procurement</td>
                        <td><span className="gate-badge-free">Instant Free Alpha</span></td>
                        <td><span className="gate-badge-paid">💳 Stripe Card / 🔷 Atlassian Invoice</span></td>
                        <td><span className="gate-badge-paid">Custom MSA / Net-30 Invoicing / SAML</span></td>
                        <td>Corporate card / Atlassian co-terming</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Customer Proof & Telemetry */}
          {activeDiligenceTab === 'proof' && (
            <div className="diligence-tab-pane">
              <div className="diligence-pane-header">
                <span className="pane-kicker">Production Telemetry &amp; 6-Spec Benchmark</span>
                <h3>$28.5K Audited MRR, 150% NDR &amp; Empirical Production Benchmarks</h3>
                <p>
                  Reconciled against September 30, 2026 Stripe merchant statements and verified production telemetry across 30 paying organizations (26 Team, 4 Enterprise) and 38 active engineering squads.
                </p>
              </div>

              {/* Audited Financials Bridge Banner (Sept 30, 2026 Stripe Reconciliations) */}
              <div className="telemetry-banner" style={{ margin: '0 0 28px 0' }}>
                <div className="telemetry-header-row">
                  <div className="telemetry-live-badge">
                    <span className="telemetry-live-dot"></span>
                    <span>Audited Financials: Sept 30, 2026 Stripe Reconciliations</span>
                  </div>
                  <span className="telemetry-source-tag">
                    Silicon Valley Bank &amp; Stripe Merchant Statements on File
                  </span>
                </div>
                <div className="telemetry-grid">
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">$28,500</div>
                    <div className="telemetry-stat-lbl">Audited MRR ($342K ARR, +42.5% MoM)</div>
                  </div>
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">30 Orgs</div>
                    <div className="telemetry-stat-lbl">Paying Customers (26 Team / 4 Enterprise)</div>
                  </div>
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">150% NDR</div>
                    <div className="telemetry-stat-lbl">3-Month Cohort Net Dollar Retention</div>
                  </div>
                  <div className="telemetry-stat-card">
                    <div className="telemetry-stat-num">0.88 Mo</div>
                    <div className="telemetry-stat-lbl">CAC Payback ($600 CAC / $680 Mo Margin)</div>
                  </div>
                </div>

                {/* Longitudinal Cohort Retention & Engagement Telemetry */}
                <div className="telemetry-cohort-strip">
                  <div className="telemetry-cohort-card">
                    <div className="cohort-num-row">
                      <span className="cohort-num">83.7%</span>
                      <span className="cohort-benchmark">Top Decile B2B</span>
                    </div>
                    <div className="cohort-lbl">WAU / MAU Ratio (142 DAU / 385 WAU / 460 MAU)</div>
                  </div>
                  <div className="telemetry-cohort-card">
                    <div className="cohort-num-row">
                      <span className="cohort-num">90.7%</span>
                      <span className="cohort-benchmark">High SaaS Margin</span>
                    </div>
                    <div className="cohort-lbl">Gross Software Margin (Stripe merchant statements)</div>
                  </div>
                  <div className="telemetry-cohort-card">
                    <div className="cohort-num-row">
                      <span className="cohort-num">57%</span>
                      <span className="cohort-benchmark">PLG Flywheel</span>
                    </div>
                    <div className="cohort-lbl">Zero-Touch Conversions from Free CLI &amp; Cursor MCP</div>
                  </div>
                  <div className="telemetry-cohort-card">
                    <div className="cohort-num-row">
                      <span className="cohort-num">81.8%</span>
                      <span className="cohort-benchmark">9 of 11 Pilots</span>
                    </div>
                    <div className="cohort-lbl">Pilot-to-Paid Win Rate across Mid-Market Engineering</div>
                  </div>
                </div>
              </div>

              {/* Audited 6-Spec Production Benchmark Strip */}
              <div className="diligence-pane-header" style={{ marginBottom: '14px', marginTop: '10px' }}>
                <span className="pane-kicker">Empirical Production Benchmark</span>
                <h4>Audited 6-Spec Benchmark: 5 B2B SaaS Teams · 30 RFCs / 210 Stories</h4>
              </div>

              <div className="discovery-stats-banner">
                <div className="discovery-stat-item">
                  <div className="discovery-stat-val">120m → 43m</div>
                  <div className="discovery-stat-label">Planning Overhead (-64.2% cut)</div>
                </div>
                <div className="discovery-stat-item">
                  <div className="discovery-stat-val">98.4%</div>
                  <div className="discovery-stat-label">Spec Coverage (0 critical omissions)</div>
                </div>
                <div className="discovery-stat-item">
                  <div className="discovery-stat-val">46% → 81%</div>
                  <div className="discovery-stat-label">First-Pass Agent PR Merge Rate</div>
                </div>
                <div className="discovery-stat-item">
                  <div className="discovery-stat-val">98.2%</div>
                  <div className="discovery-stat-label">CI Contract Gate Precision (0 false alarms)</div>
                </div>
              </div>

              {/* Customer References Grid */}
              <div className="quotes-grid" style={{ marginTop: '28px' }}>
                <div className="quote-card">
                  <p className="quote-text">
                    "Ascona caught a breaking OAuth schema drift across 14 microservices before our Cursor agents wrote a single line of backend code. Our agent PR merge rate went from 48% to 84% in three sprints."
                  </p>
                  <div className="quote-author-wrap">
                    <div className="quote-author">Marcus Vance</div>
                    <div className="quote-org">VP of Engineering · VeloceCloud (55 Engineers · Enterprise Tier)</div>
                  </div>
                </div>

                <div className="quote-card">
                  <p className="quote-text">
                    "Sprint planning used to eat 4 hours every other Monday. With Ascona's Gherkin criteria and multi-repo AST indexing, our grooming dropped to 45 minutes and mid-sprint scope churn fell by 54%."
                  </p>
                  <div className="quote-author-wrap">
                    <div className="quote-author">Elena Rostova</div>
                    <div className="quote-org">CTO · MetricFlow (32 Engineers · Team 18 Seats)</div>
                  </div>
                </div>

                <div className="quote-card">
                  <p className="quote-text">
                    "Atlassian Intelligence only rewrites sentences. Ascona actually understands software architecture—it caught a missing webhook replay queue and decomposed it into a dedicated Spike before we even started coding."
                  </p>
                  <div className="quote-author-wrap">
                    <div className="quote-author">Staff Backend Architect</div>
                    <div className="quote-org">Enterprise Cloud Platform · 120 Engineers</div>
                  </div>
                </div>
              </div>

              {/* 3-Stage Bottoms-Up to Top-Down Expansion Funnel */}
              <div className="funnel-expansion-box" style={{ marginTop: '28px' }}>
                <div className="funnel-header">
                  <h3>3-Stage Bottoms-Up to Top-Down Expansion Funnel</h3>
                  <p>
                    How Ascona captures individual developers inside Cursor for $0, scales organically across engineering squads, and converts into $6,000–$27,000/year annual contracts co-termed on Atlassian invoices.
                  </p>
                </div>
                <div className="funnel-stages-row">
                  <div className="funnel-step-card">
                    <span className="funnel-step-badge">Stage 1 · Developer Seed</span>
                    <h4 className="funnel-step-title">Local IDE &amp; CLI Adoption ($0)</h4>
                    <p className="funnel-step-desc">
                      Tech leads install <code>npm i -g @ascona/cli</code> or connect <code>@ascona/mcp-server</code> in Cursor. &lt;8 min time-to-first-spec with zero procurement friction. Drives 57% of paid conversions.
                    </p>
                    <div className="funnel-step-metric">Organic Growth: 0 CAC developer viral loop</div>
                  </div>

                  <div className="funnel-step-card">
                    <span className="funnel-step-badge">Stage 2 · Squad Standardization</span>
                    <h4 className="funnel-step-title">Sprint Grooming &amp; CI Gate</h4>
                    <p className="funnel-step-desc">
                      Tech lead introduces Ascona during sprint planning. Planning overhead drops from 120 min to 43 min. Squad adopts the GitHub Actions CI contract gate (<code>ascona-action</code>) to govern agent PRs.
                    </p>
                    <div className="funnel-step-metric">High Win Rate: 81.8% pilot-to-paid conversion</div>
                  </div>

                  <div className="funnel-step-card">
                    <span className="funnel-step-badge">Stage 3 · Top-Down Enterprise Contract</span>
                    <h4 className="funnel-step-title">Engineering Leadership Licensing ($6k–$27k/yr)</h4>
                    <p className="funnel-step-desc">
                      VP of Engineering approves the $6,000/yr base Team Workspace or $27,000/yr Enterprise tier, co-termed directly onto their existing Atlassian invoice (App ID: 3398389661) with zero new vendor onboarding.
                    </p>
                    <div className="funnel-step-metric">Fast Payback: 0.88-month CAC payback · 150% NDR</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Enterprise Governance & Security */}
          {activeDiligenceTab === 'governance' && (
            <div className="diligence-tab-pane">
              <div className="diligence-pane-header">
                <span className="pane-kicker">Enterprise Security &amp; Compliance</span>
                <h3>Bank-Grade Compliance, Zero Data Retention &amp; Private VPC Architecture</h3>
                <p>
                  Ascona is engineered for security-sensitive engineering organizations. We operate an ephemeral memory inference pipeline with Zero Data Retention, AES-256 KMS encryption, and verified Jira Cloud &amp; Data Center compatibility.
                </p>
              </div>

              <div className="capital-allocation-grid">
                {/* Card 1: Zero Data Retention (ZDR) */}
                <div className="allocation-card">
                  <div>
                    <span className="allocation-badge target">Zero Data Retention</span>
                    <h3 className="allocation-title">Ephemeral Memory Inference</h3>
                    <p className="allocation-desc">
                      Raw customer PRDs, RFCs, and git AST diffs are processed in ephemeral RAM and discarded immediately upon response completion. Customer proprietary code is never stored, logged, or used to fine-tune shared foundation models.
                    </p>
                  </div>
                  <div className="allocation-stat-box">
                    <span className="allocation-stat-val">Zero Storage</span>
                    <span className="allocation-stat-lbl">RAM-Only Processing · AES-256 KMS · TLS 1.3</span>
                  </div>
                </div>

                {/* Card 2: Formal SOC 2 Type II Engagement */}
                <div className="allocation-card">
                  <div>
                    <span className="allocation-badge governance">Governance &amp; InfoSec</span>
                    <h3 className="allocation-title">Formal SOC 2 Type II Engagement</h3>
                    <p className="allocation-desc">
                      Formal audit engagement letter executed with an accredited third-party CPA auditing firm. Enterprise role-based access control (RBAC), SSO / SAML 2.0 (Okta, Azure AD), comprehensive audit logs, and 99.9% uptime SLA.
                    </p>
                  </div>
                  <div className="allocation-stat-box">
                    <span className="allocation-stat-val">Active CPA Engagement</span>
                    <span className="allocation-stat-lbl">SOC 2 Type II Audit Letter on File</span>
                  </div>
                </div>

                {/* Card 3: Dedicated Private VPC & Jira Data Center */}
                <div className="allocation-card">
                  <div>
                    <span className="allocation-badge capital">Infrastructure Sovereignty</span>
                    <h3 className="allocation-title">Private VPC &amp; On-Prem Data Center</h3>
                    <p className="allocation-desc">
                      Deployable within your isolated AWS / GCP VPC via Terraform modules or private Kubernetes cluster. Native support for both Jira Cloud (REST v3 &amp; Forge) and on-premises Jira Data Center instances behind enterprise firewalls.
                    </p>
                  </div>
                  <div className="allocation-stat-box">
                    <span className="allocation-stat-val">Isolated Tenancy</span>
                    <span className="allocation-stat-lbl">AWS GovCloud / Dedicated VPC / Jira Data Center</span>
                  </div>
                </div>
              </div>

              {/* Enterprise Security & Architecture Comparison Table */}
              <div className="funnel-gate-box" style={{ marginTop: '28px' }}>
                <h4 className="funnel-gate-title">
                  <span>🔒</span> Enterprise Security &amp; Compliance Specifications
                </h4>
                <div className="gate-table-wrapper">
                  <table className="funnel-gate-table">
                    <thead>
                      <tr>
                        <th>Security Capability</th>
                        <th>Free CLI &amp; Cursor MCP ($0)</th>
                        <th>Team Workspace ($6,000/yr Base)</th>
                        <th>Enterprise Control ($27,000/yr)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Code Retention Policy</strong></td>
                        <td><span className="gate-badge-paid">Zero Storage (Local Only)</span></td>
                        <td><span className="gate-badge-paid">Ephemeral RAM (Zero Retention)</span></td>
                        <td><span className="gate-badge-paid">Zero Retention + Customer KMS Key</span></td>
                      </tr>
                      <tr>
                        <td><strong>LLM Model Training</strong></td>
                        <td><span className="gate-badge-paid">Never Trained</span></td>
                        <td><span className="gate-badge-paid">Never Trained</span></td>
                        <td><span className="gate-badge-paid">Isolated Sovereign LLM / Self-Hosted</span></td>
                      </tr>
                      <tr>
                        <td><strong>Encryption Standards</strong></td>
                        <td>Local File Permissions</td>
                        <td><span className="gate-badge-paid">AES-256 KMS / TLS 1.3</span></td>
                        <td><span className="gate-badge-paid">BYOK (Bring Your Own Key) KMS</span></td>
                      </tr>
                      <tr>
                        <td><strong>Identity &amp; Access</strong></td>
                        <td>GitHub Personal Access Token</td>
                        <td>Atlassian OAuth 2.0 / Google SSO</td>
                        <td><span className="gate-badge-paid">SAML 2.0 / Okta / Azure AD / SCIM</span></td>
                      </tr>
                      <tr>
                        <td><strong>Deployment Topology</strong></td>
                        <td>Local Developer Machine</td>
                        <td>Multi-Tenant High Availability Cloud</td>
                        <td><span className="gate-badge-paid">Dedicated Private VPC / Jira Data Center</span></td>
                      </tr>
                      <tr>
                        <td><strong>Support &amp; SLA</strong></td>
                        <td>Community &amp; GitHub Issues</td>
                        <td>Priority Email (4hr SLA)</td>
                        <td><span className="gate-badge-paid">Dedicated Slack Channel · 99.9% SLA · FDE</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Enterprise Security Packet Callout Strip */}
              <div className="capital-callout-strip" style={{ marginTop: '28px' }}>
                <div className="capital-callout-text">
                  <h4>Enterprise Security &amp; Architecture Review</h4>
                  <p>
                    Review our Zero Data Retention architecture, cryptographic KMS specification, and formal CPA audit engagement letter. Custom Vendor Security Assessments (SIG Lite / VSAQ) completed within 48 hours.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <a
                    href="#signup"
                    onClick={scrollTo('signup')}
                    className="btn-primary-gradient btn-sm"
                  >
                    Request Enterprise Security Packet →
                  </a>
                  <a
                    href="/userGuide.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary-dark btn-sm"
                  >
                    📖 Read Security Spec ↗
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section id="roi" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Interactive Business Case</span>
          <h2 className="section-title">Calculate Your Organization’s Exact Savings</h2>
          <p className="section-desc">
            Engineering teams spend up to 20% of their sprint cycle on grooming, scoping, and translating PRDs into tickets. Adjust the sliders below to see your annual impact.
          </p>
        </div>

        <div className="roi-card">
          <div>
            <div className="roi-slider-group">
              <div className="slider-label-row">
                <span style={{ fontWeight: 600, color: '#f1f5f9' }}>Engineering Squad Size</span>
                <span className="slider-val-badge">{teamSize} members</span>
              </div>
              <input
                type="range"
                min="5"
                max="150"
                step="1"
                value={teamSize}
                onChange={(e) => setTeamSize(parseInt(e.target.value))}
                className="roi-range"
              />
            </div>

            <div className="roi-slider-group">
              <div className="slider-label-row">
                <span style={{ fontWeight: 600, color: '#f1f5f9' }}>Average Fully-Loaded Salary</span>
                <span className="slider-val-badge">${avgSalary.toLocaleString()} / yr</span>
              </div>
              <input
                type="range"
                min="100000"
                max="250000"
                step="5000"
                value={avgSalary}
                onChange={(e) => setAvgSalary(parseInt(e.target.value))}
                className="roi-range"
              />
            </div>

            <div className="roi-slider-group">
              <div className="slider-label-row">
                <span style={{ fontWeight: 600, color: '#f1f5f9' }}>Weekly Grooming Hours Saved / Member</span>
                <span className="slider-val-badge">{groomingHoursPerWeek} hrs / week</span>
              </div>
              <input
                type="range"
                min="1"
                max="8"
                step="1"
                value={groomingHoursPerWeek}
                onChange={(e) => setGroomingHoursPerWeek(parseInt(e.target.value))}
                className="roi-range"
              />
            </div>

            <div style={{ fontSize: '0.85rem', color: '#94a3b8', lineHeight: 1.5, marginTop: '12px' }}>
              *Based on 48 working weeks per year and an average loaded engineering hourly rate of ${(avgSalary / 2000).toFixed(0)}/hr.
            </div>
          </div>

          <div className="roi-results-box">
            <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: '8px' }}>
              Estimated Annual Savings
            </div>
            <div className="roi-highlight-number">
              ${annualDollarsSaved.toLocaleString()}
            </div>
            <div className="roi-substat">
              Equivalent to <strong>{hoursSavedPerYear.toLocaleString()} Engineering Hours</strong> reclaimed every year for product development.
            </div>
            <a href="#signup" onClick={scrollTo('signup')} className="btn-primary-gradient btn-sm" style={{ width: '100%' }}>
              Lock In Pilot Savings →
            </a>
          </div>
        </div>
      </section>

      {/* Competitive Matrix Section */}
      <section id="comparison" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Passive Tracking vs. Autonomous Synthesis</span>
          <h2 className="section-title">Where Ascona Fits: Passive Tracking vs. Autonomous Synthesis</h2>
          <p className="section-desc">
            Conventional issue trackers—from Atlassian Jira and Linear to GitHub Projects—are passive tracking containers. They provide blank boards and expect engineering managers to manually type tickets, format acceptance criteria, and guess story points. Ascona AI is the generative upstream engine that turns raw specifications into estimated, testable Jira backlogs in 30 seconds.
          </p>
        </div>

        {/* Integration Wedge Notice */}
        <div className="integration-wedge-banner">
          <span style={{ fontSize: '1.2rem' }}>⚡</span>
          <div className="integration-wedge-text">
            <strong>Integration, Not Replacement:</strong> Ascona sits upstream as the intelligent orchestration layer. We enhance your existing Atlassian Jira, Linear, and GitHub workspaces via direct bi-directional sync—<strong>zero tool migration or database exports required</strong>.
          </div>
        </div>

        {/* Mobile Swipe Hint */}
        <div className="table-mobile-hint">
          <span>👈 Swipe horizontally to view full matrix 👉</span>
        </div>

        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Capability / Workflow</th>
                <th className="th-ascona">Ascona AI</th>
                <th>Atlassian Jira (System of Record)</th>
                <th>Linear (Dev Velocity Board)</th>
                <th>Atlassian Rovo (Cloud Search Agent)</th>
                <th className="th-github">GitHub Projects</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Core Architecture Role</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> Upstream Orchestration & Decomposition Engine</td>
                <td>Entrenched Enterprise System of Record</td>
                <td>Fast Developer Issue Tracking Board</td>
                <td>Chat & Search Agent across Atlassian Cloud</td>
                <td className="td-github">Repository Issue Container</td>
              </tr>
              <tr>
                <td><strong>Autonomous PRD-to-Epic Decomposition</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> Autonomous hierarchy (30s from raw spec)</td>
                <td><span className="check-no">✕</span> Manual ticket creation; Atlassian Intelligence only rewrites single fields</td>
                <td><span className="check-no">✕</span> Manual markdown typing and issue filing</td>
                <td><span className="check-partial">~</span> Q&A on existing docs; cannot construct structured backlogs</td>
                <td className="td-github"><span className="check-no">✕</span> Blank table / manual card entry</td>
              </tr>
              <tr>
                <td><strong>Bi-Directional Issue Sync & Enrichment</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> Native 2-way REST sync with custom Jira/Linear schema mapping</td>
                <td>Native host (requires manual administrative field maintenance)</td>
                <td><span className="check-no">✕</span> Isolated database (requires replacing Jira)</td>
                <td>Native to Jira Cloud only (search-focused)</td>
                <td className="td-github"><span className="check-partial">~</span> GitHub repositories only</td>
              </tr>
              <tr>
                <td><strong>Automated Gherkin BDD Acceptance Criteria</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> Given-When-Then testable scenarios for happy/edge cases</td>
                <td><span className="check-no">✕</span> Free-form text box requiring manual engineer write-up</td>
                <td><span className="check-no">✕</span> Plain markdown description</td>
                <td><span className="check-no">✕</span> Not supported</td>
                <td className="td-github"><span className="check-no">✕</span> Plain markdown text box</td>
              </tr>
              <tr>
                <td><strong>Predictive Sprint Velocity & 16-Field Matrix</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> 16-field Agile matrix with Fibonacci points & resource balancing</td>
                <td><span className="check-partial">~</span> Historical velocity charts (requires manual poker meetings)</td>
                <td><span className="check-partial">~</span> Manual cycle estimates & automated rollups</td>
                <td><span className="check-no">✕</span> None</td>
                <td className="td-github"><span className="check-no">✕</span> Basic milestones & dates</td>
              </tr>
              <tr>
                <td><strong>Working Backwards (Amazon PRFAQ & RFC)</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> Built-in Press Release, Customer FAQ & Tech RFC generator</td>
                <td><span className="check-no">✕</span> None (Confluence templates require manual writing)</td>
                <td><span className="check-no">✕</span> None</td>
                <td><span className="check-partial">~</span> Summarizes existing pages only</td>
                <td className="td-github"><span className="check-no">✕</span> None</td>
              </tr>
              <tr>
                <td><strong>Developer-First CLI & Model Context Protocol (MCP)</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> Native CLI tool + MCP Server for Cursor, Claude & IDEs</td>
                <td><span className="check-no">✕</span> Heavy web portal & raw REST API only</td>
                <td><span className="check-partial">~</span> Keyboard shortcuts / web app only</td>
                <td><span className="check-no">✕</span> Chat sidebar in Atlassian web apps only</td>
                <td className="td-github"><span className="check-partial">~</span> GitHub CLI (issues only, no AI backlog)</td>
              </tr>
              <tr>
                <td><strong>Zero LLM Data Retention & Privacy</strong></td>
                <td className="td-ascona"><span className="check-yes">✓</span> Zero data retention; PRDs & code never train foundation models</td>
                <td><span className="check-yes">✓</span> Atlassian Trust framework</td>
                <td><span className="check-yes">✓</span> Standard SaaS privacy</td>
                <td><span className="check-yes">✓</span> Atlassian Cloud boundary</td>
                <td className="td-github"><span className="check-yes">✓</span> GitHub Enterprise privacy</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Enterprise Security & Data Governance Architecture */}
      <section id="security" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Bank-Grade Compliance &amp; Governance</span>
          <h2 className="section-title">Enterprise Security Architecture: Zero Data Retention by Design</h2>
          <p className="section-desc">
            Engineered from Day 1 to satisfy strict InfoSec audits across regulated Fintech, Healthcare, and Enterprise SaaS organizations. Your intellectual property, proprietary codebase ASTs, and technical specs remain strictly sovereign.
          </p>
        </div>

        {/* 4 Security Pillars Grid */}
        <div className="security-pillars-grid">
          <div className="security-pillar-card">
            <div className="security-icon-circle">🔒</div>
            <h3 className="security-pillar-title">Zero Data Retention (ZDR)</h3>
            <p className="security-pillar-desc">
              Customer prompts, PRDs, and decomposed backlog tickets are processed ephemerally in RAM and purged immediately post-inference. Data is never stored on disk and never used to train foundation models.
            </p>
            <span className="security-check-tag">✓ Ephemeral Inference RAM</span>
          </div>

          <div className="security-pillar-card">
            <div className="security-icon-circle">🛡️</div>
            <h3 className="security-pillar-title">AES-256 &amp; TLS 1.3 Encryption</h3>
            <p className="security-pillar-desc">
              All payload transfers require modern TLS 1.3 with strict cipher suites and HSTS enforcement. Persistent credentials and API tokens are safeguarded using envelope encryption via KMS with AES-256 keys.
            </p>
            <span className="security-check-tag">✓ FIPS 140-2 Compliant KMS</span>
          </div>

          <div className="security-pillar-card">
            <div className="security-icon-circle">📜</div>
            <h3 className="security-pillar-title">SOC 2 Type II Engagement &amp; Audit Logs</h3>
            <p className="security-pillar-desc">
              Formal audit engagement letter active with accredited third-party CPA auditing firm. Every backlog decomposition, export event, and schema alteration is cryptographically logged in an append-only ledger.
            </p>
            <span className="security-check-tag">✓ CPA Engagement Letter on File</span>
          </div>

          <div className="security-pillar-card">
            <div className="security-icon-circle">🏢</div>
            <h3 className="security-pillar-title">Okta, SAML 2.0 &amp; SCIM Provisioning</h3>
            <p className="security-pillar-desc">
              Enterprise identity integration via SAML 2.0 and automated SCIM user lifecycle management. Enforce mandatory corporate MFA, session revocation, and fine-grained role-based access controls (RBAC).
            </p>
            <span className="security-check-tag">✓ Just-in-Time Role Bindings</span>
          </div>
        </div>

        {/* Security Whitepaper Callout Strip */}
        <div className="whitepaper-callout-strip">
          <div className="whitepaper-text-group">
            <h4>Conducting InfoSec or CISO Vendor Review?</h4>
            <p>
              Review our architectural data flow diagrams, formal SOC 2 Type II engagement letter, zero-retention subprocessor disclosures, and threat assessment model in our technical documentation.
            </p>
          </div>
          <a
            href="/userGuide.html#security"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary-dark btn-sm"
          >
            Read Security Architecture Spec ↗
          </a>
        </div>
      </section>

      {/* Transparent Commercial Pricing Tiers Section */}
      <section id="pricing" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Transparent Commercial Architecture</span>
          <h2 className="section-title">Dual-Lever Enterprise Pricing: Predictable &amp; Scalable</h2>
          <p className="section-desc">
            Deploy Ascona across your engineering squads. Sits directly on your existing Jira and GitHub licenses with zero per-ticket surprise fees.
          </p>
        </div>

        {/* Annual / Monthly Toggle */}
        <div className="pricing-billing-toggle">
          <button
            type="button"
            className={`billing-toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
            onClick={() => setBillingCycle('annual')}
          >
            Annual Billing <span className="discount-pill">Save 20%</span>
          </button>
          <button
            type="button"
            className={`billing-toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly Billing
          </button>
        </div>

        {/* Pricing Cards Grid */}
        <div className="pricing-grid">
          {/* Tier 1: Developer PLG */}
          <div className="pricing-card">
            <div>
              <h3 className="pricing-tier-name">Developer PLG</h3>
              <p className="pricing-tier-tagline">For individual tech leads &amp; open source maintainers testing autonomous backlog generation locally.</p>
              <div className="pricing-cost-row">
                <span className="pricing-cost-number">$0</span>
                <span className="pricing-cost-period">/ month</span>
              </div>
              <div className="pricing-billed-note">Free forever · Single-repo AST</div>
              <ul className="pricing-features-list">
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Up to 25 AI spec decompositions / month</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Single-repo AST codebase indexing</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Terminal CLI (<code>npm i -g @ascona/cli</code>)</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Model Context Protocol (MCP) server for Cursor &amp; Claude Code</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>GitHub Issues &amp; Markdown export</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Community Slack &amp; GitHub Discussions support</span></li>
              </ul>
            </div>
            <a href="#simulator" onClick={scrollTo('simulator')} className="btn-secondary-dark btn-sm" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              Test Free Simulator ↓
            </a>
          </div>

          {/* Tier 2: Team Workspace ($6,000/year Base Anchor) */}
          <div className="pricing-card popular">
            <span className="popular-badge">★ Recommended for Engineering Orgs</span>
            <div>
              <div className="self-serve-pill">
                💳 Instant Stripe Self-Serve Checkout · 🔷 1-Click Atlassian Invoice
              </div>
              <h3 className="pricing-tier-name">Team Workspace</h3>
              <p className="pricing-tier-tagline">For high-velocity product &amp; engineering squads seeking to eliminate 10+ hours of grooming weekly.</p>
              <div className="pricing-cost-row">
                <span className="pricing-cost-number">{billingCycle === 'annual' ? '$500' : '$625'}</span>
                <span className="pricing-cost-period">/ month</span>
              </div>
              <div className="pricing-billed-note">
                {billingCycle === 'annual'
                  ? '$6,000 base billed annually (10 seats included · $50/extra seat/mo · blended avg $750/mo = $9K ACV)'
                  : '$625 base billed monthly (10 seats included · $60/extra seat/mo)'}
              </div>
              <div className="pricing-marketplace-note">
                <span>🔷</span>
                <span>
                  <strong>Atlassian Marketplace Procurement:</strong> Co-termed directly onto your existing Atlassian invoice (App ID: <code>3398389661</code>) with zero new vendor procurement friction.
                </span>
              </div>
              <ul className="pricing-features-list">
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span><strong>Unlimited</strong> Epics, User Stories &amp; Gherkin scenarios</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span><strong>Multi-Repo AST Indexing</strong> (up to 10 microservices)</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span><strong>Direct Bi-Directional Jira Cloud Sync (REST v3)</strong> &amp; Linear sync</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span><strong>Deterministic GitHub Actions CI Contract Gate</strong> (<code>ascona-action</code>)</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Amazon PRFAQ &amp; RFC technical spec synthesizer</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>16-field Agile sprint matrix &amp; resource balancing</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Shared Bayesian Fibonacci story point calibration</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>10 team member seats included ($50/extra seat/mo)</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Slack workflow integration &amp; approval triggers</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Priority 4-hour support SLA</span></li>
              </ul>
            </div>
            <a href="#signup" onClick={scrollTo('signup')} className="btn-primary-gradient btn-sm" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              ⚡ Request Private Pilot (14-Day Trial) →
            </a>
          </div>

          {/* Tier 3: Enterprise Control Tier ($27,000/year) */}
          <div className="pricing-card">
            <div>
              <h3 className="pricing-tier-name">Enterprise Control</h3>
              <p className="pricing-tier-tagline">For regulated engineering organizations requiring cross-repo governance across 3–50+ codebases.</p>
              <div className="pricing-cost-row">
                <span className="pricing-cost-number">$2,250</span>
                <span className="pricing-cost-period">/ month</span>
              </div>
              <div className="pricing-billed-note">$27,000 billed annually · Dedicated tenant &amp; multi-repo scale</div>
              <ul className="pricing-features-list">
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Unlimited teams, squads &amp; multi-org workspaces</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span><strong>Full Cross-Repo AST Contract Enforcement</strong> (3–50+ repos)</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span><strong>Jira Data Center on-premise &amp; private VPC deployment</strong></span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Custom Jira custom field mapping &amp; enterprise transition gates</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Zero Data Retention (ZDR) guarantee &amp; SOC 2 Type II audit report</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>SAML SSO / Okta / Azure AD SCIM user provisioning</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>Dedicated Forward-Deployed Engineer (FDE) onboarding</span></li>
                <li className="pricing-feature-item"><span className="check-icon">✓</span> <span>99.9% uptime SLA &amp; customized enterprise MSA</span></li>
              </ul>
            </div>
            <a href="#signup" onClick={scrollTo('signup')} className="btn-secondary-dark btn-sm" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
              Contact Enterprise Sales →
            </a>
          </div>
        </div>

        {/* Security & Integration Badges Strip */}
        <div className="security-trust-strip">
          <div className="security-badge-item">🛡️ SOC 2 Type II Readiness</div>
          <div className="security-badge-item">🔒 Zero LLM Data Retention Guarantee</div>
          <div className="security-badge-item">🔷 Verified Atlassian Marketplace Partner</div>
          <div className="security-badge-item">🐙 Verified GitHub App & Webhook Pipeline</div>
          <div className="security-badge-item">⚡ Linear Integrations Directory</div>
          <div className="security-badge-item">🔐 SAML SSO & Okta SCIM Ready</div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-wrapper">
        <div className="section-header">
          <span className="section-tag">Enterprise FAQ</span>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-desc">
            Everything you need to know about integrating Ascona into your current sprint workflow.
          </p>
        </div>

        <div className="faq-list">
          {[
            {
              q: "How does Ascona AI integrate with our existing Jira Cloud instance?",
              a: "Ascona connects securely via Atlassian OAuth 2.0 or scoped REST API tokens. It can read project configurations, custom issue types, sprint boards, and custom fields to ensure all generated issues match your organization's exact schema."
            },
            {
              q: "How does the Ascona MCP Server work with Cursor and Claude Desktop?",
              a: "The Ascona Model Context Protocol (MCP) server runs locally via npx (@ascona/mcp-server). It gives your AI coding assistant direct tool calling capabilities to query Jira issues, fetch acceptance criteria, generate subtasks, and calculate story points without ever leaving your editor."
            },
            {
              q: "Does Ascona support Jira Data Center and on-premise deployments?",
              a: "Yes. In addition to Jira Cloud, Ascona provides dedicated enterprise connectors for Jira Data Center with VPC peering, self-hosted proxy agents, and air-gapped deployment options."
            },
            {
              q: "Can Ascona handle non-standard requirements like Figma files or Slack threads?",
              a: "Yes. In addition to standard PRDs and markdown briefs, Ascona includes multi-modal adapters that extract functional specifications from Figma component annotations, Confluence spaces, and Slack design discussions."
            },
            {
              q: "Is our proprietary codebase or product roadmap safe?",
              a: "Absolutely. Ascona is designed for zero data retention. We never use your confidential specifications, architecture documents, or repository data to train foundation models. All data is encrypted in transit (TLS 1.3) and at rest (AES-256) with SOC2 Type II compliance."
            },
            {
              q: "How is Ascona AI fundamentally different from GitHub Projects, Issues, and Milestones?",
              a: "GitHub Projects (tables, boards, task lists) and GitHub Issues are passive tracking containers. They provide empty buckets and require product managers and engineers to spend 15+ hours weekly manually typing issue titles, drafting descriptions, creating sub-issues, and guessing story points. Ascona is the autonomous generative engine upstream: you feed it a raw PRD, Notion spec, or technical brief, and it autonomously extracts architectural domains, structures Epics and Persona User Stories ('As a [Persona], I want [Feature], so that [Value]'), drafts testable Given-When-Then Gherkin acceptance criteria, calibrates Fibonacci complexity, and creates a 16-field sprint plan in 30 seconds. Ascona then syncs those completed tickets directly into Jira Cloud, Jira Data Center, or GitHub Issues."
            },
            {
              q: "Can Ascona export generated backlogs directly into GitHub Issues and GitHub Projects?",
              a: "Yes! While Ascona offers deep two-way native sync with Atlassian Jira Cloud and Data Center, you can also use our @ascona/cli or Model Context Protocol (MCP) server to push generated Epics, Stories, and sub-issues directly into GitHub Repositories and GitHub Projects boards via the GitHub REST API ($ ascona parse ./spec.md --sync-github --repo org/repo)."
            },
            {
              q: "How accurate are the calibrated story point estimates?",
              a: "Ascona utilizes a Bayesian calibration model that incorporates your team's historical sprint velocity, schema migration complexity, and third-party API dependencies. Teams using Ascona report an 82% reduction in sprint rollover caused by estimation mismatch."
            }
          ].map((item, idx) => (
            <div key={idx} className={`faq-item ${openFaq === idx ? 'open' : ''}`}>
              <button
                type="button"
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
              >
                <span>{item.q}</span>
                <span className="faq-icon">{openFaq === idx ? '✕' : '+'}</span>
              </button>
              {openFaq === idx && (
                <div className="faq-answer">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Enterprise Pilot / Sign Up Section */}
      <section id="signup" className="signup-section">
        <div className="signup-card">
          {submitted ? (
            <div className="success-card">
              <div className="success-icon">✓</div>
              <h3 className="success-title">Pilot Application Received!</h3>
              <p className="success-desc">
                Thank you, <strong>{formData.name}</strong>. We've reserved your team’s spot in our Priority Enterprise Pilot Queue.
                A solutions architect will reach out to <strong>{formData.email}</strong> within 24 hours with your Jira sandbox provisioning key.
              </p>
              <div style={{ marginTop: '24px' }}>
                <a href="#simulator" onClick={scrollTo('simulator')} className="btn-secondary-dark btn-sm">
                  Back to Simulator
                </a>
              </div>
            </div>
          ) : (
            <>
              <h2>Request Private Pilot for Engineering Teams</h2>
              <p>
                2-week hands-on pilot on your active Jira or GitHub instance. We configure custom issue schemas and run alongside your next sprint kickoff with zero tool disruption.
              </p>

              {submitError && (
                <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', color: '#fca5a5', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="signup-form-grid">
                  <div>
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-input"
                      placeholder="Alex Chen"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Work Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      placeholder="alex@company.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Company Name</label>
                    <input
                      type="text"
                      name="company"
                      className="form-input"
                      placeholder="Acme Technologies"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Primary Role</label>
                    <select
                      name="role"
                      className="form-select"
                      value={formData.role}
                      onChange={handleInputChange}
                    >
                      <option value="VP of Engineering">VP of Engineering / Head of Eng</option>
                      <option value="CTO / Founder">CTO / Technical Co-Founder</option>
                      <option value="Head of Product">Head of Product / Lead PM</option>
                      <option value="Product Manager">Product Manager</option>
                      <option value="Engineering Manager">Engineering Manager</option>
                      <option value="Staff / Principal Engineer">Staff / Principal Engineer</option>
                      <option value="Agile Coach / Scrum Master">Agile Coach / Scrum Master</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Engineering Team Size</label>
                    <select
                      name="teamSize"
                      className="form-select"
                      value={formData.teamSize}
                      onChange={handleInputChange}
                    >
                      <option value="1-10">&lt; 10 engineers</option>
                      <option value="10-50">10 – 50 engineers</option>
                      <option value="50-200">50 – 200 engineers</option>
                      <option value="200+">200+ engineers (Enterprise)</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Current Issue Tracker</label>
                    <select
                      name="currentTool"
                      className="form-select"
                      value={formData.currentTool}
                      onChange={handleInputChange}
                    >
                      <option value="Jira Cloud">Atlassian Jira Cloud</option>
                      <option value="Jira Data Center">Jira Data Center (On-Prem)</option>
                      <option value="Linear">Linear</option>
                      <option value="Azure DevOps">Azure DevOps</option>
                      <option value="GitHub Projects">GitHub Projects</option>
                      <option value="Other">Other / Multiple</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary-gradient btn-lg"
                  style={{ width: '100%', maxWidth: '420px', margin: '0 auto', display: 'block' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting Application...' : '⚡ Request Private Pilot (2-Week Trial)'}
                </button>
              </form>

              <div className="privacy-badge">
                <span>🔒 Zero-Data Retention Guarantee</span>
                <span>•</span>
                <span>SOC2 Type II Ready</span>
                <span>•</span>
                <span>Jira Cloud & Data Center Verified</span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Multi-Column Footer */}
      <footer className="main-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="brand-icon" style={{ width: '28px', height: '28px', fontSize: '0.9rem' }}>A</div>
              <span style={{ fontWeight: 800, color: '#fff', fontSize: '1.15rem' }}>Ascona AI</span>
            </div>
            <p className="footer-copy">
              The Spec-to-PR Control Plane. Governing engineering scope and deterministic CI contract gates across human developers and coding agents.
            </p>
          </div>

          <div className="footer-links-group">
            <div className="footer-col">
              <h6>Product</h6>
              <ul>
                <li><a href="#simulator" onClick={scrollTo('simulator')}>Live Simulator</a></li>
                <li><a href="#product-tour" onClick={scrollTo('product-tour')}>Product Tour</a></li>
                <li><a href="#cli-mcp" onClick={scrollTo('cli-mcp')}>Terminal CLI &amp; MCP</a></li>
                <li><a href="#diligence" onClick={scrollTo('diligence')}>Architecture &amp; Moat</a></li>
                <li><a href="#comparison" onClick={scrollTo('comparison')}>Compare vs Jira &amp; Linear</a></li>
                <li><a href="#pricing" onClick={scrollTo('pricing')}>Pricing ($6K/yr Base)</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h6>Resources &amp; Docs</h6>
              <ul>
                <li><a href="/userGuide.html" target="_blank" rel="noopener noreferrer">User Guide &amp; API ↗</a></li>
                <li><a href="/userGuide.html#mcp" target="_blank" rel="noopener noreferrer">MCP Server Schemas ↗</a></li>
                <li><a href="/userGuide.html#cli" target="_blank" rel="noopener noreferrer">CLI Commands Reference ↗</a></li>
                <li><a href="#faq" onClick={scrollTo('faq')}>Enterprise FAQ</a></li>
                <li><a href="#roi" onClick={scrollTo('roi')}>ROI &amp; Savings Calculator</a></li>
                <li><a href="https://marketplace.atlassian.com/apps/3398389661/ascona-ai" target="_blank" rel="noopener noreferrer">Atlassian Marketplace (v3.1) ↗</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h6>Trust &amp; Governance</h6>
              <ul>
                <li><a href="#diligence" onClick={() => { setActiveDiligenceTab('governance'); const el = document.getElementById('diligence'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>Zero Data Retention (ZDR)</a></li>
                <li><a href="#diligence" onClick={() => { setActiveDiligenceTab('governance'); const el = document.getElementById('diligence'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>SOC 2 Type II Engagement</a></li>
                <li><a href="https://marketplace.atlassian.com/apps/3398389661/ascona-ai" target="_blank" rel="noopener noreferrer">Atlassian Marketplace Partner</a></li>
                <li><a href="https://github.com/marketplace/actions/ascona-action" target="_blank" rel="noopener noreferrer">Verified GitHub Action</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '36px', paddingTop: '20px', borderTop: '1px solid var(--border-subtle)', fontSize: '0.82rem', color: '#64748b' }}>
          &copy; 2026 Ascona AI Inc. All rights reserved. · Atlassian Marketplace Partner ID 3398389661 · The Spec-to-PR Control Plane
        </div>
      </footer>
    </div>
  );
}

export default App;
